import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getDockBoardData } from '@/api/client';
import { WorkTruckMark } from '@/components/WorkTruckMark';
import { PauseMark } from '@/components/PauseMark';
import type { BoardApptDto, BoardDoorDto, DockBoardDto } from '@/api/types';
import { Timer } from 'lucide-react';
import { detentionState, detentionLabel, isDetentionRed, settingsFor, type DetentionSettings, type DetentionState } from '@/lib/detention';

/**
 * ---- ⭐ F114 — DETENTION ON THE FLOOR BOARD (Decision 298, 2026-09-29) ----------
 *
 * > Pete: *"speaking of the floor board, they definitely need to be made aware of
 * > detention trucks. Can you suggest how that would look?"* → popup (with mockups)
 * > → *"Clock on every truck + header count"*, confirmed twice.
 *
 * ⭐ THE DRIVER CARD'S RULE, NOT A SECOND ONE: `detentionState` (web/src/lib/detention.ts)
 * with the facility's settings from the board payload — clock from the later of
 * arrival and booked time (day-only: arrival), orange inside the free time, red past
 * it. ⛔ Only an ARRIVED appointment with an arrival stamp is on the clock (F110b: an
 * Approved one has not arrived yet, so it has no clock to show; a check-in with no
 * appointment never reaches the board at all).
 * ⭐ Every truck on the clock shows it — door cards, on-deck and waiting-for-door
 * chips; a card whose truck is RED gets a solid red outline; the header carries the
 * same count the check-in panel does. ⛔ The FLASHING band stays the door-idle alert:
 * two alarms that look alike stop being read as either.
 *
 * ⭐ F155 (Decision 329) — THE CLOCK COUNTS DOWN. Pete: *"start at his appointment time
 * and count down from there. Should only be 1-hour … Turn orange at 30 and red at 15"*;
 * above 30 left it is *"Same color as the status - arrived"*; at zero it counts up
 * "over" in red. The level comes from `detentionState`, the words from `detentionLabel`
 * — the check-in card uses both, so the wall and the scheduler never disagree.
 */
function boardClock(appt: BoardApptDto | null, det: DetentionSettings | null, nowMs: number): DetentionState | null {
    if (!appt || !det || appt.status !== 'arrived' || !appt.arrivalTime) return null;
    const st = detentionState({
        arrivalIso: appt.arrivalTime,
        scheduledStartIso: appt.scheduledTime,
        timed: appt.schedulingMode !== 'DayOnly',
        departureIso: null,
    }, settingsFor(det, appt.clientCode, appt.direction), nowMs);   // ⭐ F173: this truck's client's free time
    return st && st.level !== 'off' ? st : null;
}

/** The pill's colour class for a level: arrived colour → orange → red. */
const detTone = (level: DetentionState['level']): string =>
    isDetentionRed(level) ? 'pill-det-over' : level === 'warn' ? 'pill-det-near' : 'pill-det-ok';

function DetentionPill({ st }: { st: DetentionState }) {
    return (
        <span className={`pill pill-det ${detTone(st.level)}`}
              title={st.level === 'over' ? 'In detention' : 'Time left before detention'}>
            <Timer className="det-ico" aria-hidden="true" />
            {detentionLabel(st)}
        </span>
    );
}

/**
 * WAREHOUSE DOCK WORKER BOARD — the wall-mounted, read-only kiosk (backlog #8).
 *
 * Route: /board  (public — no Entra, no carrier session)
 *
 * AUTH: none in the browser. The backend (GET kiosk/board) authorizes by the
 * device's facility-network egress IP and derives which facility from it, so
 * this page sends no facility id and holds no token. A 403 means "this TV isn't
 * on a registered facility network".
 *
 * DESIGN: ported from the approved mockup (dock-board-mockup.html, 2026-08-10).
 * Read from 15–40 ft, in motion, for ~2 seconds — it answers "which door, what's
 * there, is it here yet?". Dark base cuts glare on an always-on wall TV and lets
 * the status colors carry from distance; the door number is the anchor; ARRIVED
 * is green ("work now") and expected is dim; the amber "waiting for door" strip
 * is deliberately secondary and hides itself when empty. Fluid type + auto-fill
 * grid means one build fits an 80" TV or reflows onto a tablet.
 *
 * PER-SCREEN CONFIG (dock.BoardConfig, migration 136):
 *   ?cfg=west    names this screen's SAVED config — the same way the labour
 *                clock and the driver sign-in are opened by their own URLs.
 *                Omitted → the facility's default config → shipped defaults.
 *                The config arrives inside the board payload, one round trip.
 *
 * The old query params still work and OVERRIDE the saved config, deliberately:
 * they are how you try a setting on a laptop before committing it to a screen
 * everyone can see. A TV should carry only ?cfg=.
 *   ?doors=N ?rotate=S ?refresh=S ?holding=0 ?open=0 ?group=EAST ?scale=roomy
 *
 * Nothing here is secret — the IP gate is what authorizes; these only choose
 * what this particular screen shows.
 */

const DEFAULT_DOORS_PER_PAGE = 12;
const DEFAULT_ROTATE_SECONDS = 10;
const DEFAULT_REFRESH_SECONDS = 15;
/** Past this with no successful fetch, the updated stamp goes amber. */
const STALE_AFTER_MS = 60_000;

/** Every absent field renders as "--" (fields are captured, not guaranteed). */
function v(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') return '--';
    return String(value);
}

/**
 * Type-scale tiers for the door grid (see BOARD_CSS). Fewer cards → bigger card
 * → bigger type. Card COUNT picks the tier we'd like; the page then measures
 * itself and steps down if that didn't fit (see the fit-to-screen effect), so a
 * 1024px tablet and an 80" TV both end up as large as they can be without
 * pushing anything off the bottom. A wall display nobody can scroll must never
 * overflow.
 */
const TIERS = [undefined, 'roomy', 'sparse'] as const;
/** A config (or ?scale=) can PIN a tier; 'auto' means pick by card count. */
const TIER_INDEX: Record<string, number> = { base: 0, roomy: 1, sparse: 2 };

function wantedTier(cardCount: number): number {
    if (cardCount === 0) return 0;
    if (cardCount <= 2) return 2;
    if (cardCount <= 6) return 1;
    return 0;
}

/** The value if it has any content, else null — for lines that hide when empty. */
function blank(value: string | null | undefined): string | null {
    const t = value?.trim();
    return t ? t : null;
}

/** "9:02a" in facility-local time. */
function fmtTime(iso: string | null, tz: string): string {
    if (!iso) return '--';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '--';
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true,
    }).formatToParts(d);
    const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
    const minute = parts.find((p) => p.type === 'minute')?.value ?? '';
    const ap = (parts.find((p) => p.type === 'dayPeriod')?.value ?? '').toLowerCase().startsWith('p') ? 'p' : 'a';
    return `${hour}:${minute}${ap}`;
}

function fmtClock(at: Date, tz: string): string {
    return new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true,
    }).format(at);
}

function fmtStamp(iso: string, tz: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '--';
    const s = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: false,
    }).format(d);
    // en-US h23 pads to "09:14:32"; the approved design shows "9:14:32".
    return s.replace(/^0/, '');
}

/**
 * A URL flag that can turn a config setting BOTH ways. Absent → the config wins;
 * present → the param wins. `x=0` / `x=false` are off, anything else is on.
 * (An earlier cut ANDed the two, so ?open=1 silently could not re-enable a strip
 * a config had switched off — a param that can only ever say "no" is not an
 * override.)
 */
function boolParam(raw: string | null, fallback: boolean): boolean {
    if (raw === null) return fallback;
    const v = raw.trim().toLowerCase();
    return !(v === '0' || v === 'false' || v === 'no' || v === 'off');
}

function intParam(raw: string | null, fallback: number, min: number, max: number): number {
    const n = raw === null ? NaN : Number.parseInt(raw, 10);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(min, n));
}

/** Header subtitle: the warehouse side and the door span this page is showing. */
function pageSubtitle(doors: BoardDoorDto[]): string {
    if (doors.length === 0) return 'no doors';
    const groups = Array.from(new Set(doors.map((d) => d.doorGroup).filter((g): g is string => !!g)));
    const side = groups.length === 1 ? groups[0] : null;
    const span = doors.length === 1
        ? `door ${doors[0].doorLabel}`
        : `doors ${doors[0].doorLabel}–${doors[doors.length - 1].doorLabel}`;
    return side ? `${side} · ${span}` : span;
}

/**
 * Direction, and while a worker is on it, the LIVE activity: "↓ UNLOAD" becomes
 * "↓ UNLOADING" and breathes. The status pill still says ARRIVED because the
 * truck IS still arrived — this pill is what changes when work starts, which is
 * the one thing on the card that differs minute to minute.
 *
 * A soft opacity fade, not a hard blink: this is an always-on wall display, and
 * a strobing element is both irritating up close and an accessibility problem.
 */
function DirectionPill({ direction, working }: { direction: BoardApptDto['direction']; working?: boolean }) {
    const cls = `pill ${direction === 'load' ? 'pill-load' : 'pill-unload'}${working ? ' pill-active' : ''}`;
    const arrow = direction === 'load' ? '↑' : '↓';
    const word = direction === 'load'
        ? (working ? 'LOADING' : 'LOAD')
        : (working ? 'UNLOADING' : 'UNLOAD');
    return <span className={cls}>{arrow} {word}</span>;
}

/**
 * The board's rendering of the shared "being worked" mark. Sizing and the orbit
 * animation come from BOARD_CSS (--f-truck, db-orbit, and its reduced-motion
 * rule); the glyph itself is WorkTruckMark, shared with the scheduler.
 *
 * Motion, not colour, is what says "this truck is being worked": the card keeps
 * its ARRIVED colour because the truck IS still arrived. A rotating icon is
 * self-explanatory to a loader who never reads a legend, and movement is
 * detectable from further away than any hue.
 */
function WorkingTruck() {
    return (
        <WorkTruckMark
            wrapClassName="worktruck"
            ringClassName="worktruck-ring"
            truckClassName="worktruck-truck"
        />
    );
}

/**
 * ---- ⭐⭐ THE PAUSED MARK (Pete, 2026-09-24) -----------------------------
 *
 * > *"Put that smbol where ever it belongs on Pause in the floor board,
 * > laborclock, dock schdule or labor panel."*
 *
 * ⛔⛔ THE BOARD HAD NO WAY TO SAY IT. `cardState` asked `labor?.state ===
 * 'working'`, which means "the assignment is OPEN" — so a paused truck kept the
 * spinning truck mark and read as work in progress from forty feet.
 *
 * ⭐ A GLYPH, NOT A COLOUR, AND THAT IS THIS FILE'S OWN RULE. `card-working`
 * carries the note: *"BEING WORKED keeps the ARRIVED colour on purpose — the
 * truck IS still arrived. The orbiting-truck mark carries 'being worked', so the
 * board never says with colour what it already says with motion."* Paused obeys
 * the same rule with a static mark in place of the motion, which is why it needs
 * no new palette entry — and this board already spends amber twice (RESERVED and
 * HOLDING).
 *   ** A NEW STATE'S COLOUR MUST BE FREE, NOT MERELY APPROPRIATE. A SHAPE IS
 *      ALWAYS FREE. **
 *
 * ⚠️ SAME `.worktruck` BOX, so the door column does not reflow when a worker
 * steps off. ⛔ AND IT DOES NOT SPIN: the content of this mark is that the motion
 * stopped.
 */
function PausedTruck() {
    return (
        <PauseMark
            wrapClassName="worktruck"
            markClassName="worktruck-pause"
        />
    );
}

/**
 * How a card reads. Labor outranks status: a truck being unloaded is 'working'
 * whatever its status says, and a fully-worked truck reads 'done' until someone
 * marks it departed.
 */
type CardState = 'arrived' | 'working' | 'paused' | 'done' | 'idle' | 'blocked' | 'reserved' | 'standdown';

/**
 * How the DOOR reads. A block covering now outranks whatever is parked there:
 * the door itself is the broken thing, and that has to be visible before a
 * loader walks to it. The truck (if any) still renders inside the card.
 *
 * An empty door dedicated to a client right now is 'reserved' — not free, not
 * broken. It earns a card because "don't back into DD120, it's TRAFFIX's" is
 * exactly the kind of thing the floor needs before it makes the mistake.
 */
function doorState(door: BoardDoorDto, showReserved = true): CardState {
    if (door.blocked) return 'blocked';
    // A stand-down does not hold a door against a live dedication: if the only
    // thing at this door is a dead appointment, the door IS free, and "reserved
    // for TRAFFIX" is the more actionable truth. The stand-down still renders
    // inside the card body.
    const liveHere = (a: BoardApptDto | null) =>
        !!a && a.status !== 'noshow' && a.status !== 'cancelled';
    if (showReserved && !liveHere(door.current) && !liveHere(door.onDeck) && door.dedicatedTo) {
        return 'reserved';
    }
    return cardState(door.current);
}

function cardState(appt: BoardApptDto | null): CardState {
    if (!appt) return 'idle';
    // A STAND-DOWN OUTRANKS LABOUR, unlike everything else here. Labour normally
    // wins because "someone is unloading it" is the most useful thing you can
    // say about a truck — but a no-showed or cancelled appointment is over, and
    // showing a work state on it would be a lie about the floor. This can happen
    // for real: a truck gets worked, then the appointment is cancelled to void
    // the booking.
    if (appt.status === 'noshow' || appt.status === 'cancelled') return 'standdown';
    /**
     * ⭐⭐ THE HONEST STATE FIRST (2026-09-24). `labor.state` means "the
     * assignment is OPEN"; `assignmentState` means "is anybody actually on it".
     * Since migration 201 those are different questions and pausing changes only
     * the second.
     * ⚠️ `assignmentState` IS undefined ON THE READER'S LOWER RUNGS, which is
     * why the old two-state test stays underneath as the fallback. ⛔ Absent must
     * degrade to what the board did yesterday, never to 'paused'.
     *   ** A NEW FIELD'S ABSENCE MUST MEAN "I DO NOT KNOW", AND "I DO NOT KNOW"
     *      MUST NEVER RENDER AS A CONFIDENT NEW ANSWER. **
     */
    const st = appt.labor?.assignmentState;
    if (st === 'Active') return 'working';
    /* ⚠️ ON BREAK DRAWS THE SAME CARD. A card has room for one mark, and to a
       loader forty feet away both mean "nobody is on this truck right now". The
       Labor rail, which has room for a word, says which. */
    if (st === 'Paused' || st === 'OnBreak') return 'paused';
    if (appt.labor?.state === 'working') return 'working';
    if (appt.labor?.state === 'done') return 'done';
    return appt.status === 'arrived' ? 'arrived' : 'idle';
}

/**
 * "just now" / "14m ago" / "1h 20m ago" — how long since a stand-down died.
 *
 * A wall display can't be asked to subtract two clock times from across the
 * room, and the whole point of a stand-down is that RECENCY is the information:
 * a truck that no-showed two minutes ago is news, one from an hour ago is
 * housekeeping. So the card states the elapsed time outright.
 */
/**
 * ⭐ MINUTES SINCE THE DOOR WAS ASSIGNED. ⛔ Returns -1 for a missing or unparseable
 * stamp so a caller comparing `> threshold` can never be tricked into firing by a
 * value that means "we do not know".
 *   ** A SENTINEL FOR "UNKNOWN" MUST FAIL THE TEST, NOT PASS IT. **
 */
function doorIdleMinutes(iso: string | null | undefined, nowMs: number): number {
    if (!iso) return -1;
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return -1;
    return (nowMs - t) / 60000;
}

function agoLabel(iso: string | null, nowMs: number): string | null {
    if (!iso) return null;
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return null;
    const mins = Math.floor((nowMs - t) / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h}h ago` : `${h}h ${m}m ago`;
}

/** The appointment block inside a door card (current slot or on-deck slot). */
function ApptBlock(
    { appt, state, tz, nowMs, detention = null }:
    { appt: BoardApptDto | null; state: CardState; tz: string; nowMs: number; detention?: DetentionSettings | null },
) {
    if (!appt) return <div className="empty">—</div>;

    // HEADLINE = whatever actually identifies this truck to the floor. Brighton
    // populates neither ClientName nor CarrierName, so the approved layout
    // rendered its biggest line as "--" and put the one number anyone can use
    // (the container/PO) in the small print. Fall back to the reference, and
    // then don't repeat it below. Lines with nothing in them are dropped
    // outright rather than printed as dashes — dead text costs the same wall
    // space as live text.
    // WHO the truck is for, then what identifies it. ClientName first, then the
    // ClientCode (carrier-booked appointments resolved a code from the PO but
    // stored no name until 2026-08-13), then the reference — Brighton populates
    // neither client field, and a container number beats an empty headline.
    const client = blank(appt.clientName) ?? blank(appt.clientCode);
    const headline = client ?? blank(appt.referenceNumber);
    const headlineIsRef = !client && !!blank(appt.referenceNumber);
    const carrier = blank(appt.carrierName);
    const driver  = blank(appt.driverName);

    const stood = state === 'standdown';

    // 'done' recedes to the muted treatment: worked, still here, not the thing
    // anyone needs to act on.
    /* ⚠⭐ PAUSED IS **LIT**. The truck is at the door and the card is the arrived
       colour, so its text must be the arrived text — dimming it would make a
       truck standing at a door read like an expected one that has not turned up. */
    /* ⚠️ 'done' IS NOW LIT TOO, and it MUST be: the card below went from a dark
       worked-tint to the arrived treatment, and dim text on it would be unreadable.
       ** A COLOUR CHANGE AND THE TEXT CONTRAST THAT DEPENDS ON IT ARE ONE CHANGE. ** */
    const lit = state === 'arrived' || state === 'working' || state === 'paused' || state === 'done';
    const cCls = lit ? 'client-arrived' : 'client-idle';
    const mCls = lit ? 'meta-arrived' : 'meta-idle';
    const fCls = lit ? 'facts-arrived' : 'facts-idle';
    const sCls = lit ? 'strong-arrived' : 'strong-idle';

    const worked = appt.direction === 'load' ? 'LOADED' : 'UNLOADED';

    /**
     * ---- ⭐⭐⭐ THE TIME ROW: FOUR SLOTS, NOT ONE SENTENCE (Pete, 2026-09-24) -----
     *
     * > *"I thought we were having Arr 9:30am Started 9:45am, Elapsed 15:00."*
     *
     * ⛔⛔ ~~ONE MUTUALLY-EXCLUSIVE STRING~~. `timeLabel` used to be a five-way
     * ternary producing exactly ONE of Arr / Started / Finished / Sched — so a card
     * showing `Started 11:43a` had silently dropped its arrival, and no card anywhere
     * on the board carried an elapsed clock at all. That was not a bug; it was built
     * that way, and the layout it produced hid the fact:
     *   ** A FIELD THAT IS SOMETIMES ONE THING AND SOMETIMES ANOTHER LOOKS COMPLETE
     *      FROM EVERY SINGLE CARD AND IS INCOMPLETE ACROSS ALL OF THEM. **
     *
     * ⭐ FOUR SLOTS NOW, IN A FIXED ORDER, AND EACH ONE ALWAYS OCCUPIES ITS COLUMN:
     *
     *     Arr 8:53a  |  Started 9:12a  |  Finished 11:04a  |  1h 52m
     *
     * Pete's choice, offered all three shapes: *"All four - Arr, Started, Finished,
     * Total."* The fourth slot is the DURATION -- it counts up while the work runs
     * and freezes into the total when it finishes, so the column means one thing.
     *
     * ---- ⭐⭐ ELAPSED IS WALL CLOCK FROM `Started`, AND KEEPS RUNNING WHEN PAUSED --
     *
     * Pete, offered worked-time and freeze-on-pause as alternatives: **"Keeps running
     * from Started."** The question this board answers is *how long has this door been
     * tied up*, and a door is tied up through lunch.
     *
     * ⚠️⚠️ **SO THIS NUMBER AND THE LABOR RAIL'S WILL DISAGREE ON A PAUSED TRUCK, AND
     * THAT IS DELIBERATE.** The rail shows `workedSeconds` -- Pete's call this morning:
     * *"Worked time, with a paused-since line"* -- which excludes breaks because job
     * costing needs the hours somebody actually stood there. Two questions, two
     * numbers. ⛔ THEY THEREFORE MUST NOT SHARE A WORD: this column says **Elapsed**
     * and the rail says **Worked**, and neither may ever be relabelled to the other.
     *   ** TWO NUMBERS ABOUT ONE TRUCK ARE SAFE ONLY WHILE THEIR LABELS MAKE IT
     *      OBVIOUS THEY ARE ANSWERING DIFFERENT QUESTIONS. THE DAY BOTH SAY "TIME",
     *      SOMEBODY RECONCILES THEM AND FILES A BUG. **
     *
     * ⛔ A STAND-DOWN STILL COLLAPSES TO ONE CELL. "Sched 11:15" on a cancelled card
     * reads like a truck that is still coming, so it says when it DIED instead -- and
     * a no-show has no arrival, no start and no duration, so three empty columns
     * beside it would be three questions nobody asked.
     */
    const startedUtc  = appt.labor?.startedUtc  ?? null;
    const finishedUtc = appt.labor?.finishedUtc ?? null;
    /* ⚠️ THE END OF THE MEASUREMENT IS THE FINISH IF THERE IS ONE, ELSE NOW. ⛔ Not
       `nowMs` always: a card that finished at 11:04 would otherwise keep counting all
       afternoon, which is the same defect as a clock with no off switch. */
    const durationMs = (() => {
        if (!startedUtc) return null;
        const s = Date.parse(startedUtc);
        if (Number.isNaN(s)) return null;
        const e = finishedUtc ? Date.parse(finishedUtc) : nowMs;
        if (Number.isNaN(e)) return null;
        return Math.max(0, e - s);
    })();
    /** "15m" / "1h 52m". ⚠️ null when there is no start -- never "0m", which is a claim. */
    const durationLabel = durationMs === null ? null
        : durationMs < 3_600_000
            ? `${Math.floor(durationMs / 60_000)}m`
            : `${Math.floor(durationMs / 3_600_000)}h ${String(Math.floor((durationMs % 3_600_000) / 60_000)).padStart(2, '0')}m`;

    const standLabel = agoLabel(appt.standDownUtc, nowMs) ?? `Sched ${fmtTime(appt.scheduledTime, tz)}`;
    /* ⛔ A CARD THAT HAS NOT ARRIVED HAS NOTHING TO PUT IN ANY OF THE FOUR. It gets
       the scheduled time in slot one and three empty columns -- the slots stay, so the
       row still lines up with the card beside it. */
    const notHere = !(state === 'arrived' || state === 'working' || state === 'paused' || state === 'done');

    const workers = appt.labor?.workers ?? [];

    return (
        <>
            <div className="pills">
                {/**
                  * ⭐⭐ **'done' JOINS THE LIST** (Pete, 2026-09-24): *"the 2nd
                  * screenshot show say ARRIVED UNLOADED just like the first screen shot
                  * to keep it consistent."*
                  *
                  * ⛔ AND IT IS NOT MERELY CONSISTENCY — IT IS THE TRUTH THIS WHOLE
                  * CHANGE IS ABOUT. The truck IS still arrived; the status only moves on
                  * DEPARTURE. Dropping the ARRIVED pill when the work finished made the
                  * card look like the load had moved on when it had not.
                  *   ** IF A SECOND FACT IS ADDED BECAUSE THE FIRST ONE IS STILL TRUE,
                  *      THE FIRST ONE MUST NOT DISAPPEAR WHEN THE SECOND ARRIVES. **
                  *
                  * ⚠️ NONE OF THESE FOUR CHECK `appt.status`, and that is pre-existing:
                  * a working card has shown ARRIVED since the board was built, even on a
                  * booking never marked arrived — because labour starting IS an arrival
                  * in practice. Adding a status test for 'done' alone would make this one
                  * branch stricter than its three neighbours for no reason a reader
                  * could find.
                  */}
                {(state === 'arrived' || state === 'working' || state === 'paused' || state === 'done') && (
                    <span className="pill pill-arrived">ARRIVED</span>
                )}
                {/* ⭐ F114 — the detention clock, beside ARRIVED (Decision 298). */}
                {(() => { const st = boardClock(appt, detention, nowMs); return st ? <DetentionPill st={st} /> : null; })()}
                {/**
                  * ---- ⛔⛤⚤ ~~the PAUSED pill~~ — REMOVED THE DAY IT SHIPPED ---------
                  *
                  * > Pete, 2026-09-24: *"we are showing status in two spots. One above
                  * > the DD08 and then one above ANDMED"* → *"Are the status's to the
                  * > left, more line in-progress/working and the one above ANDMED more
                  * > like complete status?"*
                  *
                  * ⭐⭐ HE NAMED A RULE THIS CARD ALREADY HALF-OBEYED, and it is worth
                  * stating plainly because everything on this card now follows it:
                  *
                  *     THE DOOR BLOCK (left) answers  — IS ANYBODY ON THIS TRUCK NOW?
                  *                                       orbiting truck / pause mark / nothing
                  *     THE PILLS (above the client)   — WHERE IS THIS LOAD IN ITS LIFE?
                  *                                       EXPECTED -> ARRIVED -> UNLOADED
                  *
                  * ⛔ I ADDED A PAUSED PILL THIS MORNING AND IT BROKE THE SPLIT. Paused
                  * is a live-labour fact, so it belongs on the left — and with the mark
                  * ALSO drawing it, one fact was speaking in both rows at once.
                  *   ** TWO CHANNELS ARE ONLY WORTH HAVING WHILE THEY ANSWER TWO
                  *      QUESTIONS. THE MOMENT ONE FACT APPEARS IN BOTH, A READER STOPS
                  *      TRUSTING EITHER TO BE COMPLETE. **
                  *
                  * ⚠️ THE PILL'S CSS (`.pill-paused`) SURVIVES, unused. It is four lines,
                  * and the next person to want a word here should find the treatment
                  * already decided rather than invent a second one.
                  */}
                {state === 'done' && <span className="pill pill-done">{worked}</span>}
                {/* ⚠️ TWO WORDS FOR ONE CARD STATE. Both are 'idle' — no truck, no
                    labour — but EXPECTED means the office agreed and REQUESTED means
                    it has not. The floor plans differently for each, so the card says
                    which even though the treatment is the same.
                      ** ONE VISUAL STATE MAY CARRY TWO WORDS. ONE WORD MUST NOT CARRY
                         TWO STATES. ** */}
                {state === 'idle' && (
                    <span className="pill pill-expected">
                        {appt.status === 'requested' ? 'REQUESTED' : 'EXPECTED'}
                    </span>
                )}
                {stood && (
                    <span className="pill pill-standdown">
                        {appt.status === 'noshow' ? 'NO SHOW' : 'CANCELLED'}
                    </span>
                )}
                {/* No direction pill on a stand-down: nothing is going to be
                    loaded or unloaded, so IN/OUT is noise on a dead card. */}
                {state !== 'done' && !stood && (
                    /**
                     * ---- ⛔⛤⛤ I REMOVED THE TENSE AND PETE PUT IT BACK (2026-09-24) ----
                     *
                     * > *"the unloading blinking sign and the truck circle are great -
                     * > please keep that as is"*
                     *
                     * ⛔ I HAD DROPPED `working` HERE, so the pill read UNLOAD rather than
                     * UNLOADING. The reasoning was clean: a continuous tense is a claim
                     * about RIGHT NOW, and "right now" is the door block's channel — so
                     * the tense was the last live-labour fact in the lifecycle row.
                     *
                     * ⚠️ AND HE HAD APPROVED IT, in a popup, where it was one clause
                     * inside the option he picked. Then he saw the board and said keep it.
                     *   ** AN OPTION'S DESCRIPTION IS NOT THE SAME AS SEEING THE THING.
                     *      A CHANGE BUNDLED INTO A CHOICE ABOUT SOMETHING ELSE HAS NOT
                     *      REALLY BEEN CHOSEN. **
                     *   ** AND THE MAN WHO READS THIS BOARD EVERY DAY OUTRANKS A RULE I
                     *      DERIVED FROM IT AN HOUR AGO. **
                     *
                     * ⭐ SO THE SPLIT HOLDS WHERE IT WAS DUPLICATING A FACT (the PAUSED
                     * pill is gone — the pause mark already said it) AND YIELDS WHERE IT
                     * WAS ONLY TIDINESS. UNLOADING appears nowhere else on the card; it
                     * duplicated nothing.
                     */
                    <DirectionPill direction={appt.direction} working={state === 'working'} />
                )}
            </div>
            {headline && (
                <div className={`client ${cCls}${stood ? ' client-stood' : ''}`}>{headline}</div>
            )}
            {(carrier || driver) && (
                <div className={`meta ${mCls}`}>{[carrier, driver].filter(Boolean).join(' · ')}</div>
            )}
            {/**
              * ============================================================================
              * ⛔⛔⛤ THE FACT ROWS WERE A WRAPPING FLOW, AND THAT WAS THE WHOLE BUG
              * ============================================================================
              *
              * > Pete, 2026-09-24, over a screenshot of five door cards: *"Seems like the
              * > fields are not in the same place across the cards? plt is in a different
              * > spot, started time is different... I think because things are wrapping?"*
              *
              * ⭐ HE HAD IT EXACTLY. Every row here was `display:flex; flex-wrap:wrap` over
              * a list of CONDITIONAL spans, so each field's position was decided by which
              * of its neighbours happened to exist on that particular truck:
              *
              *   AVIENT has no PO          -> `plt` lands SECOND instead of third
              *   ZEVIAL has a long Ref+PO  -> `plt` and the time WRAP to a second line
              *   SCOTWD has no labour      -> the time slot says `Arr`, AVIENT's says
              *                                `Started`, and neither says both
              *
              * Nothing was ever POSITIONED. It fell into place, and it fell differently on
              * every card, which is why five cards side by side read as five layouts.
              *   ** A ROW OF OPTIONAL ITEMS IS NOT A LAYOUT. IT IS A LIST THAT HAPPENS TO
              *      LOOK LIKE ONE WHENEVER EVERY ITEM IS PRESENT. **
              *
              * ⭐⭐ SO THE ROWS ARE **GRIDS** NOW, with a fixed column per field. A field
              * with no value leaves its column EMPTY rather than collapsing it, so column
              * three is column three on all sixty-one doors.
              *
              * ⚠️ AND AN EMPTY CELL IS RENDERED DIFFERENTLY DEPENDING ON WHAT IS ABSENT,
              * which is not a flourish:
              *   - IDENTIFIERS and VEHICLE numbers print their label and `--`. Their
              *     absence means WE DO NOT KNOW THIS, and that is worth a line on a wall
              *     board because somebody can go and find out.
              *   - TIMES print NOTHING. `Finished --` on a truck still being unloaded is
              *     not missing data; the event has not happened. A label there would
              *     invite somebody to chase a stamp that does not exist yet.
              *   ** AN ABSENCE IS ONLY INFORMATION IF YOU SAY WHAT IT IS ABSENT FROM, AND
              *      "NOT YET" IS NOT THE SAME ABSENCE AS "NOBODY TOLD US". **
              *
              * ⛔ `min-width:0` ON EVERY CELL. Without it a long container number refuses
              * to shrink and pushes the grid wider than the card -- the flow layout's
              * failure mode returning through the back door.
              */}
            <div className={`facts facts-id ${fCls}`}>
                {/**
                  * ⭐⭐ **SO ON AN OUTBOUND LOAD** (spec §5.2). The field is the same
                  * `referenceNumber`; only the LABEL changes. ✅ Confirmed against live
                  * data — today's outbound rows carry SO1931641, SO1956472, SO1944747
                  * in that column.
                  * ⛔ AND THIS IS WHY THE PO/REFERENCE SPLIT MATTERED. Until today this
                  * field fell back to PoNumber when the reference was null, so labelling
                  * it "SO" on a wall board would have put a PO under the wrong word.
                  *   ** A LABEL RULE IS SAFE ONLY ON A FIELD THAT HOLDS ONE THING. **
                  * ⚠️ THE CELL SURVIVES `headlineIsRef`. When the reference has been
                  * promoted to the headline the number is not repeated here -- but the
                  * COLUMN stays, because dropping it would shift PO and plt left on
                  * exactly the Brighton cards that have no client name.
                  */}
                <span className="cell">
                    {!headlineIsRef && <>{appt.direction === 'load' ? 'SO' : 'Ref'} <span className={sCls}>{v(appt.referenceNumber)}</span></>}
                </span>
                {/* ⚠️ THE PO IS ITS OWN COLUMN. ⛔⚤ ~~shown only when there IS one~~ --
                    that suppression is what moved `plt` on every card without a PO. */}
                <span className="cell">PO <span className={sCls}>{v(appt.poNumber)}</span></span>
                <span className="cell">☰ <span className={sCls}>{v(appt.palletCount)}</span> plt</span>
            </div>
            {/* ---- the four time slots -- see the block comment on `durationLabel` ---- */}
            <div className={`facts facts-time ${fCls}`}>
                {stood ? (
                    /* ⛔ ONE CELL, SPANNING. A stood-down truck has no arrival, no start
                       and no duration; three empty columns beside the reason would be
                       three questions nobody asked. */
                    <span className="cell cell-span">{standLabel}</span>
                ) : notHere ? (
                    <>
                        <span className="cell">Sched <span className={sCls}>{fmtTime(appt.scheduledTime, tz)}</span></span>
                        <span className="cell" /><span className="cell" /><span className="cell" />
                    </>
                ) : (
                    <>
                        <span className="cell">{blank(appt.arrivalTime) && <>Arr <span className={sCls}>{fmtTime(appt.arrivalTime, tz)}</span></>}</span>
                        <span className="cell">{startedUtc && <>Started <span className={sCls}>{fmtTime(startedUtc, tz)}</span></>}</span>
                        <span className="cell">{finishedUtc && <>Finished <span className={sCls}>{fmtTime(finishedUtc, tz)}</span></>}</span>
                        {/* ⭐ THE DURATION COLUMN. Counts up while the work runs, freezes
                            at the total when it finishes -- one column, one meaning.
                            ⛔ "Elapsed", NEVER "Worked": the Labor rail's number excludes
                            breaks and this one does not. See the comment above. */}
                        <span className="cell">{durationLabel && <>Elapsed <span className={sCls}>{durationLabel}</span></>}</span>
                    </>
                )}
            </div>
            {/**
              * ---- ⭐⭐ THE NUMBERS ON THE TRUCK (spec §2.2 / §3.3) -------------------
              *
              * > Pete: *"lets make sure the cards have all the necessary information...
              * > Trailer Number/ContainerID... Truck # for the client"*
              *
              * ⚠️ A SECOND LINE, NOT MORE ITEMS IN THE FIRST. The facts row already
              * ellipsises on a narrow card, and these are the numbers a loader matches
              * against the trailer in front of him — the worst possible thing to truncate.
              *   ** THE FIELD MOST LIKELY TO BE CUT IS THE ONE THE READER CAME FOR. **
              *
              * ⚠️⚠️ FIRST PASS ON THE TIERING, AND THE SPEC SAYS SO: *"A spec that lists
              * every field is a list of what may be shown, not a layout... to be agreed
              * on screen rather than in this file."* This is here to be reacted to.
              *
              * ⛔ THE WHOLE LINE DISAPPEARS WHEN THERE IS NOTHING IN IT. A card for a
              * booking nobody has arrived against has no trailer and no ordinal, and an
              * empty labelled row is how a wall board fills with blanks.
              */}
            {/**
              * ⭐⭐ CONT STAYS HERE, BESIDE TRL -- Pete's call, 2026-09-24.
              *
              * > *"Cont should probably be by Ref and PO don't you think?"*
              *
              * ⚠️ IT ONLY LOOKED STRANDED BECAUSE ROW ONE WRAPPED OVER IT. On ZEVIAL the
              * long `Ref AD090926SY04 PO 50587200` pushed `plt` and the time onto a second
              * line, so CONT -- correctly on line three -- read as an orphan two rows below
              * the identifiers it resembles.
              *   ** A FIELD IN THE WRONG PLACE AND A FIELD PUSHED OUT OF PLACE LOOK
              *      IDENTICAL. FIX THE LAYOUT BEFORE MOVING THE FIELD. **
              *
              * ⭐ AND IT BELONGS HERE ON THE MERITS: `containerNumber` on this card is the
              * BOX AT THE DOOR, not an order number. It carries a `bookedContainerNumber`
              * disagreement flag exactly as `trailerNumber` does -- that pairing is the
              * tell. Ref/PO/SO identify the LOAD; TRL/CONT identify the EQUIPMENT.
              * ⚠️ Offered the move anyway, Pete chose *"Leave it with TRL, fix the wrap
              * first"*. If it still reads wrong on the fixed grid it moves then.
              *
              * ⛔⚤ ~~THE WHOLE ROW DISAPPEARS WHEN THERE IS NOTHING IN IT~~ -- reversed.
              * That suppression was the same fault as the PO's: a card with no trailer
              * dropped a whole row, so the workers line below it sat at a different height
              * from the card next door. The row is always present and its cells go to
              * `--`, which is the honest answer for equipment nobody has recorded.
              */}
            <div className={`facts facts-veh ${fCls}`}>
                <span className="cell">TRL <span className={sCls}>{v(appt.trailerNumber)}</span>
                    {/* ⭐ THE BOOKED VALUE, ONLY ON A DISAGREEMENT — Pete's
                        decision. ⚠️ A mismatch is NOT an error: a carrier sending
                        a different trailer is normal and the floor just needs to
                        know which one is at the door. */}
                    {blank(appt.bookedTrailerNumber) && (
                        <span className="mismatch"> (booked {appt.bookedTrailerNumber})</span>
                    )}
                </span>
                <span className="cell">CONT <span className={sCls}>{v(appt.containerNumber)}</span>
                    {blank(appt.bookedContainerNumber) && (
                        <span className="mismatch"> (booked {appt.bookedContainerNumber})</span>
                    )}
                </span>
                {/* ⭐ "Truck 2 of 3" — derived, spec §5.1. ⚠️ Ordered by SCHEDULED
                    time so it means the same thing here as on the scheduler. */}
                <span className="cell">
                    {appt.truckOfDay
                        ? <>Truck <span className={sCls}>{appt.truckOfDay}</span>
                            {appt.truckOfDayTotal ? ` of ${appt.truckOfDayTotal}` : ''}</>
                        : null}
                </span>
            </div>
            {workers.length > 0 && (
                <div className={`workers ${fCls}`}>
                    By <span className={sCls}>{workers.join(', ')}</span>
                </div>
            )}
        </>
    );
}

/**
 * Door codes vary in length by facility - Commerce City uses "31", Brighton uses
 * "DD119". Step the type down as the code gets longer so a long code still fits
 * the door block instead of overflowing it, while short codes keep the big
 * approved size.
 */
function doorNumClass(label: string): string {
    const n = label.length;
    if (n <= 2) return 'door-num';
    if (n === 3) return 'door-num door-num-3';
    if (n === 4) return 'door-num door-num-4';
    return 'door-num door-num-5';
}

/**
 * The door code, with any alpha prefix set small so the DIGITS stay huge.
 * "DD119" renders as a small "DD" beside a full-size "119" - the number is what
 * a loader reads from 40 ft, and shrinking the whole code to fit would defeat
 * that. Codes with no prefix ("31") render exactly as approved; anything that
 * doesn't split cleanly falls back to length-stepped sizing.
 */
function DoorNumber({ label }: { label: string }) {
    const m = /^([A-Za-z][A-Za-z-]*?)[- ]?(\d+)$/.exec(label);
    if (!m) return <span className={doorNumClass(label)}>{label}</span>;
    const digits = m[2];
    return (
        <span className="door-num-split">
            <span className="door-pre">{m[1]}</span>
            <span className={digits.length <= 3 ? 'door-digits' : 'door-digits door-digits-4'}>{digits}</span>
        </span>
    );
}

/**
 * A stand-down's flavour class. Sets `--sd`, the ONE variable every stand-down
 * rule below reads, so no-show and cancelled share all their geometry and differ
 * only in colour — and that colour comes from the palette (migration 138), not
 * from a literal in this file.
 *
 * Returns '' for anything that isn't a stand-down, which is also the safe answer
 * if the board ever renders one without an appointment attached.
 */
function standDownClass(appt: BoardApptDto | null): string {
    if (!appt) return '';
    if (appt.status === 'noshow')    return ' sd-noshow';
    if (appt.status === 'cancelled') return ' sd-cancelled';
    return '';
}

const CARD_CLASS: Record<CardState, string> = {
    arrived:  'card card-arrived',
    working:  'card card-working',
    /* ⭐ THE ARRIVED COLOUR, DELIBERATELY — the truck IS still arrived, and the
       pause mark carries the rest. Same reasoning as `card-working` above. */
    paused:   'card card-paused',
    done:     'card card-done',
    idle:     'card card-idle',
    blocked:  'card card-blocked',
    reserved: 'card card-reserved',
    standdown:'card card-standdown',
};
const DOOR_CLASS: Record<CardState, string> = {
    arrived:  'door door-arrived',
    working:  'door door-working',
    paused:   'door door-paused',
    done:     'door door-done',
    idle:     'door door-idle',
    blocked:  'door door-blocked',
    reserved: 'door door-reserved',
    standdown:'door door-standdown',
};

/**
 * DEDICATED-DOOR badge. Same amber chip + dot the scheduler's door column uses,
 * so "this door belongs to TRAFFIX" looks the same wherever you read it.
 */
function DedicationChip({ label }: { label: string }) {
    return (
        <span className="ded" title={`${label}-Dedicated`}>
            <span className="ded-dot" />
            <span className="ded-txt">{label}</span>
        </span>
    );
}

/** Out of service, in the scheduler's words: "BLK  <reason>" / "Out of service". */
function BlockedBlock({ block, tz }: { block: NonNullable<BoardDoorDto['blocked']>; tz: string }) {
    const label = block.label?.trim() || 'Blocked';
    // Don't say "Out of service" twice when that IS the reason someone typed.
    const sub: string[] = [];
    if (label.toLowerCase() !== 'out of service') sub.push('Out of service');
    if (block.endUtc) sub.push(`until ${fmtTime(block.endUtc, tz)}`);
    return (
        <div className="blk">
            <div className="pills">
                <span className="pill pill-blk">BLK</span>
                <span className="blk-label">{label}</span>
            </div>
            {sub.length > 0 && <div className="blk-sub">{sub.join(' · ')}</div>}
        </div>
    );
}

/**
 * ---- ⭐⭐ THE DIAGONAL STAMP (Pete, 2026-09-24) --------------------------------
 *
 * > *"Can we get a word slash across the screen saying UNLOADED (diagnoloy) as well,
 * > in bold and caps - white. Same for LOADED. This should be in front of all the
 * > other info. and go diagnol, corner to corner. That will let thme know for sure
 * > the truck is complete with their tasks (floor)"*
 * > → *"Still keep the other status items though on the card"*
 *
 * ⭐⭐ THIS IS A THIRD CHANNEL AND IT IS DELIBERATE. The board's rule has been that
 * the door block says who is on the truck and the pills say where the load is in its
 * lifecycle — both read at ARM'S LENGTH. A loader forty feet away scanning for "which
 * of these am I still needed on" reads neither: a pill is twelve pixels of text.
 *   ** A SIGNAL SIZED FOR THE PERSON AT THE SCREEN IS NOT A SIGNAL FOR THE PERSON ON
 *      THE FLOOR. DISTANCE IS ITS OWN AUDIENCE. **
 * ⚠️ So it does NOT replace the pills — Pete was explicit. It is the same fact said
 * loudly for a different reader.
 *
 * ⛔ `done` ONLY. A stamp that appeared on any other state would be the loudest thing
 * on the board saying the least.
 *
 * ⚠️ ~-20 DEGREES, NOT A TRUE CORNER-TO-CORNER DIAGONAL. Cards vary in height with
 * their contents, so the true diagonal angle differs per card and CSS cannot measure
 * it without JS. A constant angle reads as a STAMP; a per-card angle would read as a
 * rendering fault when two cards side by side tilted differently.
 *   ** "CORNER TO CORNER" IS A DESCRIPTION OF AN EFFECT, NOT A SPECIFICATION OF AN
 *      ANGLE. **
 *
 * ⛔ `pointer-events:none` — it covers the whole card and must never eat a click.
 */
function DoneStamp({ direction }: { direction: BoardApptDto['direction'] }) {
    return (
        <div className="stamp" aria-hidden="true">
            <span>{direction === 'load' ? 'LOADED' : 'UNLOADED'}</span>
        </div>
    );
}

/**
 * ---- ⭐⭐⭐ THE DOOR ALERT (Pete, 2026-09-24, spec §3.5) ----------------------
 *
 * > *"We want to make the Alert standout on a card (maybe make it blink with an alert
 * > across it or similiar)... So a truck doesn't sit for more than 15 minutes before
 * > someone is working it."*
 * > → *"make it diagnol across the floor board just like LOADED / UNLOADED"*
 *
 * ⭐ SAME MECHANISM AS THE DONE STAMP, and that is Pete's instruction and also the
 * right answer: the floor has already learned to read a diagonal word on these cards,
 * so a second one needs no explaining.
 *   ** A NEW SIGNAL THAT REUSES A LEARNED SHAPE COSTS NOTHING TO TEACH. **
 *
 * ⛔⛔ THE TWO STAMPS CANNOT COLLIDE, AND IT IS BY CONSTRUCTION RATHER THAN BY
 * CARE: the done stamp requires labour to have FINISHED and this one requires labour
 * never to have STARTED. ⚠️ If that ever stops being true, one card gets two rotated
 * words on top of each other and neither is readable.
 *
 * ⚠️⚠️ **IT BLINKS, AND IT IS THE ONLY THING ON THIS BOARD THAT MAY.** Blink is the
 * strongest signal a wall display has and the most easily spent.
 *   ** THE DAY A SECOND ALERT BLINKS, NEITHER DOES. **
 * ⚠️ `prefers-reduced-motion` STOPS THE PULSE AND KEEPS THE BAND — the alert must
 * survive somebody who cannot tolerate the motion, which means the colour and the
 * word have to carry it on their own anyway.
 *
 * ⚠️ THE WORD IS STATIC, NOT A COUNTDOWN. "NO LABOR 18m" would change every minute
 * while blinking, and two things moving at once is how a signal becomes noise. The
 * elapsed clock is on the card already.
 */
function DoorAlertStamp() {
    return (
        <div className="stamp stamp-alert" aria-hidden="true">
            <span>NO LABOR</span>
        </div>
    );
}

function DoorCard(
    { door, tz, showReserved, nowMs, alertMinutes, detention = null }:
    { door: BoardDoorDto; tz: string; showReserved: boolean; nowMs: number; alertMinutes: number; detention?: DetentionSettings | null },
) {
    const state = doorState(door, showReserved);
    // A blocked door with a truck still parked at it shows BOTH: the block on
    // top (it's why nobody can work), the truck under it (it's still there).
    const currentState = door.current ? cardState(door.current) : state;
    // On the CARD root, not the inner block: the door column and the pill both
    // read --sd, and both live inside this element.
    const sd = state === 'standdown' ? standDownClass(door.current) : '';
    /* ⭐ F114 — a solid red outline when a truck on this card is past its free time. */
    const clocks = [door.current, door.onDeck].map(a => boardClock(a, detention, nowMs)).filter((c): c is DetentionState => c !== null);
    const detOver = clocks.some(c => c.level === 'over');
    /* ⭐ F129 (Decision 307) — the DOOR COLUMN takes the timer's colour too: orange while a
       truck here is on the clock inside its free time, red once it is past it. Pete: *"make
       the left column (DD04) red like the timer and orange like the timer when its that time.
       So the floor can tell by color they have been waiting a long time"*. Same rule as the
       pill (boardClock), so the column and the pill can never disagree. */
    /* ⭐ F155 — above the orange lead the column keeps its ARRIVED colour (no override). */
    const doorDet = clocks.some(c => isDetentionRed(c.level)) ? ' door-det-over'
        : clocks.some(c => c.level === 'warn') ? ' door-det-near' : '';
    return (
        <div className={CARD_CLASS[state] + sd + (detOver ? ' card-det-over' : '')}>
            <div className={DOOR_CLASS[state] + doorDet}>
                {/* ABOVE the number, in its own half of the door block: the
                    green column is the biggest empty space on the card, and a
                    mark floating in the middle of it is visible from further
                    away than one tucked under the digits. */}
                {state === 'working' && <WorkingTruck />}
                {state === 'paused' && <PausedTruck />}
                <div className="door-id">
                    <span className="door-lbl">DOOR</span>
                    <DoorNumber label={door.doorLabel} />
                    {door.dedicatedTo && <DedicationChip label={door.dedicatedTo} />}
                </div>
            </div>
            <div className="body">
                {door.blocked && <BlockedBlock block={door.blocked} tz={tz} />}
                {state === 'reserved' ? (
                    <div className="resv">
                        <div className="resv-lbl">RESERVED</div>
                        <div className="resv-for">{door.dedicatedTo}</div>
                        <div className="resv-sub">No truck at this door</div>
                    </div>
                ) : (door.current || !door.blocked) && (
                    /* ⛔ THE WRAPPER IS NOT COSMETIC -- it is the stamp's row. See below. */
                    <div className="cur">
                        <ApptBlock appt={door.current} state={currentState} tz={tz} nowMs={nowMs} detention={detention} />
                {/**
                  * ---- ⛔⛔⛔ THE DONE STAMP BELONGS TO THE **CURRENT** TRUCK ONLY --------
                  *
                  * > Pete, 2026-09-24, on a screenshot of DD46: *"The LOADED tile on DD46 is
                  * > going across the on deck card too. When the card is split, Loaded will
                  * > have to move the right spot on the card."*
                  *
                  * ⛔⛔ IT USED TO BE THE LAST CHILD OF THE **CARD**, WITH `inset:0`. That
                  * was written when a door card held one truck, and the comment there argued
                  * -- correctly, at the time -- that it *"has to span the door column as well
                  * as the text"*. What nobody revisited is that the card ALSO holds an ON DECK
                  * block, so `inset:0` meant the stamp covered a second, different truck.
                  * On DD46 the word LOADED lay across ADMRDL's ARRIVED pill and its name.
                  *   ** AN OVERLAY'S CONTAINING BLOCK IS A CLAIM ABOUT WHAT IT DESCRIBES.
                  *      WHEN THE CONTAINER GROWS A SECOND SUBJECT, THE CLAIM BECOMES FALSE
                  *      AND NOTHING IN THE CSS SAYS SO. **
                  * ⚠️ AND IT SAID SOMETHING WRONG, NOT MERELY UNTIDY: the on-deck truck has
                  * NOT been loaded. The loudest word on the card was over the one truck it
                  * was not true of.
                  *
                  * ⭐ SO IT IS A GRID ITEM IN `.body` ROW 1 NOW -- the same row as `.cur` --
                  * and the on-deck block owns row 2. The boundary is the grid's, so it is
                  * exact and needs no measurement, and an unsplit card is unchanged because
                  * row 1 is then the whole body.
                  *
                  * ⚠️ `door.current` IS RE-TESTED even though `state === 'done'` implies it:
                  * `state` came from `doorState(...)`, which can return 'done' via
                  * `cardState(door.current)` -- so the two are linked by a function call, not
                  * by the compiler, and TypeScript cannot narrow it for us.
                  */}
                {state === 'done' && door.current && <DoneStamp direction={door.current.direction} />}
                    </div>
                )}
                {door.onDeck && (
                    <div className="ondeck">
                        <div className="ondeck-lbl">ON DECK</div>
                        <ApptBlock appt={door.onDeck} state={cardState(door.onDeck)} tz={tz} nowMs={nowMs} detention={detention} />
                    </div>
                )}
            </div>
            {/* ⛔⛤ ~~THE DONE STAMP WAS HERE~~, as the card's last child with `inset:0`.
                It moved INTO `.body` row 1 on 2026-09-24 because a split card made it
                span the on-deck truck as well. The reasoning is at its new home.
                ⚠️ THE ALERT STAMP BELOW STAYS AT CARD LEVEL, and that is not an
                oversight: it is about the DOOR standing idle, not about either truck,
                and it only fires when `!door.current.labor` -- so it can never collide
                with the done stamp, which requires labour to have finished. */}
            {/**
              * ⚠️ FOUR CONDITIONS AND EVERY ONE OF THEM IS LOAD-BEARING:
              *   alertMinutes > 0   — 0 is the facility's off switch (migration 218)
              *   doorAssignedUtc    — no stamp, no clock. ⛔ A truck whose assignment
              *                        predates 216 has no timestamp, and a NULL must
              *                        never be read as "assigned at the epoch".
              *                        ** A NULL IS AN HONEST GAP, NOT A ZERO. **
              *   !door.current.labor— nobody has EVER worked it. A paused truck has a
              *                        labor record and is not this alert's problem.
              *   past the threshold — computed from nowMs, which ticks locally, so it
              *                        fires when it is true rather than at the next poll.
              */}
            {state !== 'done' && alertMinutes > 0 && door.current?.doorAssignedUtc
                && !door.current.labor
                && doorIdleMinutes(door.current.doorAssignedUtc, nowMs) > alertMinutes
                && <DoorAlertStamp />}
        </div>
    );
}

function HoldingCard({ appt, tz, nowMs, detention = null }: { appt: BoardApptDto; tz: string; nowMs: number; detention?: DetentionSettings | null }) {
    const stood = appt.status === 'noshow' || appt.status === 'cancelled';
    /* ⭐ F114 — a truck waiting for a door is on the clock too; often the one that matters most. */
    const clock = stood ? null : boardClock(appt, detention, nowMs);
    return (
        <div className={`hcard${stood ? ' hcard-standdown' + standDownClass(appt) : ''}${clock?.level === 'over' ? ' hcard-det-over' : ''}`}>
            <div className="hc-pills">
                {stood
                    ? <span className="pill pill-standdown">{appt.status === 'noshow' ? 'NO SHOW' : 'CANCELLED'}</span>
                    : <DirectionPill direction={appt.direction} />}
                <span className="hc-arr">
                    {stood
                        ? (agoLabel(appt.standDownUtc, nowMs) ?? `Sched ${fmtTime(appt.scheduledTime, tz)}`)
                        : appt.status === 'arrived'
                        ? `Arr ${fmtTime(appt.arrivalTime, tz)}`
                        : `Sched ${fmtTime(appt.scheduledTime, tz)}`}
                </span>
                {clock && <DetentionPill st={clock} />}
            </div>
            <div className={`hc-client${stood ? ' client-stood' : ''}`}>{blank(appt.clientName) ?? blank(appt.clientCode) ?? '--'}</div>
            <div className="hc-meta">{v(appt.carrierName)} · {v(appt.driverName)}</div>
            <div className="hc-meta">
                Ref <strong>{v(appt.referenceNumber)}</strong> · ☰ <strong>{v(appt.palletCount)}</strong> plt
            </div>
        </div>
    );
}

/**
 * ⭐ F161 (Decision 334) — THE BOARD ALSO RUNS INSIDE THE SCHEDULER. Pete: *"It should
 * load under the Calendar, Reporting, Warehouse and Claude line, replacing the scheduler
 * screen. Still in scheduler, still on the same tab ... so I can click back on the
 * calendar tab"*. `embedded` draws it in the Scheduler's content area (the header and
 * the tab line stay) instead of over the whole window, and takes its config from the
 * caller rather than the Scheduler's own address bar. ⛔ The data path is unchanged:
 * `kiosk/board`, gated by the facility network — the wall TV's rule, not a second one.
 */
const NO_PARAMS = new URLSearchParams();

/**
 * ⭐ F263: `loader` replaces the TV's IP-gated fetch when the board runs inside the
 * Scheduler (the signed-in read, mgmt/board). Absent = the wall TV, unchanged.
 */
export type DockBoardLoader = (cfgKey: string | null, signal?: AbortSignal) => Promise<DockBoardDto>;

export function DockBoardPage({ embedded = false, cfg: cfgProp, loader }: { embedded?: boolean; cfg?: string; loader?: DockBoardLoader } = {}) {
    const [urlParams] = useSearchParams();
    /* ⛔ Embedded, the address bar is the SCHEDULER's; none of its query belongs to the board. */
    const params = embedded ? NO_PARAMS : urlParams;
    const cfgKey = cfgProp ?? params.get('cfg');

    const [board, setBoard] = useState<DockBoardDto | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [lastOkMs, setLastOkMs] = useState<number | null>(null);
    const [pageIndex, setPageIndex] = useState(0);
    const [nowMs, setNowMs] = useState(() => Date.now());
    // Ref so the poll effect never restarts just because data arrived.
    const hasDataRef = useRef(false);
    const pageRef = useRef<HTMLDivElement | null>(null);
    const gridRef = useRef<HTMLDivElement | null>(null);
    const [tierIdx, setTierIdx] = useState(0);
    const [gridCols, setGridCols] = useState<number | null>(null);
    const [resizeKey, setResizeKey] = useState(0);

    // EFFECTIVE SETTINGS = the saved config, with any explicit URL param winning.
    // Before the first payload lands there is no config yet, so the shipped
    // defaults stand in — which is also exactly what a facility with no config
    // gets, so there is only ever one fallback to reason about.
    const cfg = board?.config ?? null;
    const doorsPerPage   = intParam(params.get('doors'),   cfg?.doorsPerPage   ?? DEFAULT_DOORS_PER_PAGE,   1, 200);
    const rotateSeconds  = intParam(params.get('rotate'),  cfg?.rotateSeconds  ?? DEFAULT_ROTATE_SECONDS,   3, 600);
    const refreshSeconds = intParam(params.get('refresh'), cfg?.refreshSeconds ?? DEFAULT_REFRESH_SECONDS,   5, 600);
    const showHolding    = boolParam(params.get('holding'), cfg?.showHolding   ?? true);
    const showOpen       = boolParam(params.get('open'),    cfg?.showOpenDoors ?? true);
    const groupFilter    = params.get('group') ?? cfg?.doorGroup ?? null;
    // A dedicated-but-empty door: full RESERVED card, or leave it in the open
    // strip? A site that dedicates a dozen doors all day would otherwise fill
    // its grid with empty amber cards.
    const showReserved   = boolParam(params.get('reserved'), cfg?.showReservedCards ?? true);
    // Only used to decide whether the key mentions stand-downs; the SERVER decides
    // whether any actually arrive, so this never has to agree with it to be safe.
    const standDownMinutes = cfg?.standDownMinutes ?? 60;
    const scaleParam     = params.get('scale');
    const pinnedTier     = scaleParam && scaleParam in TIER_INDEX
        ? TIER_INDEX[scaleParam]
        : (cfg && cfg.textScale !== 'auto' ? TIER_INDEX[cfg.textScale] : null);

    const load = useCallback(async (signal?: AbortSignal) => {
        try {
            const data = loader ? await loader(cfgKey, signal) : await getDockBoardData(cfgKey, signal);
            if (signal?.aborted) return;
            setBoard(data);
            hasDataRef.current = true;
            setLastOkMs(Date.now());
            setError(null);
        } catch (err) {
            if (signal?.aborted) return;
            // Keep the last good board on screen — a blank wall TV is worse than
            // a slightly stale one. The updated stamp is what goes amber.
            setError(err instanceof Error ? err.message : 'Board unavailable.');
        }
    }, [cfgKey, loader]);

    useEffect(() => {
        const ctrl = new AbortController();
        void load(ctrl.signal);
        const id = window.setInterval(() => { void load(ctrl.signal); }, refreshSeconds * 1000);
        return () => { ctrl.abort(); window.clearInterval(id); };
    }, [load, refreshSeconds]);

    // Live clock (the mock ticks slowly — minutes are all it displays).
    useEffect(() => {
        const id = window.setInterval(() => setNowMs(Date.now()), 10_000);
        return () => window.clearInterval(id);
    }, []);

    const tz = board?.timezone ?? 'America/Chicago';


    const doors = useMemo(() => {
        const all = board?.doors ?? [];
        return groupFilter ? all.filter((d) => d.doorGroup === groupFilter) : all;
    }, [board, groupFilter]);

    // A door with no truck at it and none on deck earns a full card's worth of
    // wall TV for the word "—". Split them out: cards are for doors with
    // ACTIVITY, and the empty ones collapse to a name-only strip below (they
    // still have to be visible — a loader needs to know DD134 exists and is
    // free — but they cost a line, not a card).
    //
    // A BLOCKED or DEDICATED door counts as activity even with nothing at it:
    // "this door is dead" and "this door is TRAFFIX's" are both things the
    // strip's "open" wording would flatly lie about, and both are what the
    // board exists to tell the floor before someone backs a trailer in.
    /**
     * ---- ⭐⭐ A RESERVATION WITH NO TRUCK IS NOT ACTIVITY (Pete, 2026-09-24) ----
     *
     * > *"The reserved DDoors, with nothing planned for them like requested, approved
     * > or arrived, should be at the bottom where it says Open Doors... they
     * > shouldn't be in that main screen until an appointment is there."*
     *
     * ⛔⛤ `(d.dedicatedTo && showReserved)` USED TO COUNT AS ACTIVITY, and on the
     * Denver board that was **six full cards** for doors with nothing at them,
     * competing with real trucks for the same grid.
     *   ** THE MAIN GRID IS FOR WORK. A RESERVATION WITH NO TRUCK IS A PLAN, AND A
     *      PLAN COMPETING WITH WORK FOR THE SAME SPACE IS HOW THE WORK GETS MISSED. **
     *
     * ✅ IT KEEPS ITS IDENTITY DOWNSTAIRS — the open-door chip already renders
     * `DedicationChip`, so `DD55 · Safeway` reads exactly as Pete asked without a
     * second treatment being invented for it.
     *
     * ⚠️ A BLOCKED DOOR STILL COUNTS, and a stand-down still holds its card. "This
     * door is dead" is a fact about the door; "this door is Safeway's" is a fact about
     * next week.
     *
     * ⚠️⚠️ **WATCH THE ROTATION AFTER THIS.** Paging only rotates when the active
     * doors overflow one page (`doorsPerPage`, default 12). Denver had 6 empty
     * reserved cards in the grid; removing them may drop it under one page and the
     * board simply stops moving. That is CORRECT — there is nothing to rotate to —
     * but it looks like a fault to somebody who remembers it moving.
     *   ** REMOVING ITEMS FROM A PAGED LIST CAN TURN OFF A BEHAVIOUR NOBODY
     *      CONNECTED TO THE COUNT. **
     *
     * ⚠️ `showReserved` IS NOW ONLY A CARD-STATE FLAG. It still decides whether a
     * door holding a dead appointment reads as RESERVED, which is a different
     * question from whether an empty one earns a card.
     */
    const hasActivity = (d: BoardDoorDto): boolean =>
        !!(d.current || d.onDeck || d.blocked);
    const activeDoors = useMemo(() => doors.filter(hasActivity), [doors]);
    const openDoors   = useMemo(() => doors.filter((d) => !hasActivity(d)), [doors]);

    // Paging now only ever chunks doors that HAVE something at them, so an empty
    // page can no longer exist. Door order is preserved inside each page, so the
    // spatial mental model survives.
    const pages = useMemo(() => {
        const out: BoardDoorDto[][] = [];
        for (let i = 0; i < activeDoors.length; i += doorsPerPage) {
            out.push(activeDoors.slice(i, i + doorsPerPage));
        }
        return out.length > 0 ? out : [[]];
    }, [activeDoors, doorsPerPage]);

    const pageCount = pages.length;

    // Rotation — only when the doors actually overflow one page.
    useEffect(() => {
        if (pageCount <= 1) { setPageIndex(0); return; }
        const id = window.setInterval(
            () => setPageIndex((p) => (p + 1) % pageCount),
            rotateSeconds * 1000,
        );
        return () => window.clearInterval(id);
    }, [pageCount, rotateSeconds]);

    // Door count can shrink between polls; never strand the view on a dead page.
    useEffect(() => {
        setPageIndex((p) => (p >= pageCount ? 0 : p));
    }, [pageCount]);

    const visibleDoors = pages[Math.min(pageIndex, pages.length - 1)] ?? [];

    const holding = showHolding ? (board?.holding ?? []) : [];
    /* ⭐ F114 — every truck on the detention clock, across ALL the facility's doors and
       the holding strip whatever this screen shows (paging and ?holding=0 only change
       what is drawn, not what is true), each counted once. */
    const detentionCount = (() => {
        const det = board?.detention ?? null;
        const seen = new Map<number, DetentionState['level']>();
        const all = [
            ...(board?.doors ?? []).flatMap(d => [d.current, d.onDeck]),
            ...(board?.holding ?? []),
        ];
        for (const a of all) {
            const st = boardClock(a, det, nowMs);
            if (a && st && st.level !== 'off') seen.set(a.appointmentId, st.level);
        }
        const levels = [...seen.values()];
        const over = levels.filter(l => l === 'over').length;
        const red = levels.filter(isDetentionRed).length;
        const warn = levels.filter(l => l === 'warn').length;
        return { total: seen.size, over, red, warn };
    })();
    const isStale = lastOkMs !== null && nowMs - lastOkMs > STALE_AFTER_MS;

    // ---- FIT TO SCREEN -----------------------------------------------------
    // Ask for the tier the card count deserves, then measure and step down until
    // the page stops overflowing. Both effects are LAYOUT effects, so all of it
    // resolves before the browser paints — the TV never shows the too-big pass.
    // Shrinking is monotonic and bottoms out at tier 0, so this can't loop.
    const wanted = pinnedTier ?? wantedTier(visibleDoors.length);
    useLayoutEffect(() => {
        setTierIdx(wanted);
    }, [wanted, resizeKey, board?.generatedUtc, pageIndex, showOpen, showHolding, showReserved]);

    useLayoutEffect(() => {
        const el = pageRef.current;
        if (!el || tierIdx === 0) return;
        if (el.scrollHeight - el.clientHeight > 2) setTierIdx(tierIdx - 1);
    });

    useEffect(() => {
        const onResize = () => setResizeKey((k) => k + 1);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // ---- BALANCED COLUMNS --------------------------------------------------
    // auto-fit packs as many columns as fit, so 6 cards on a wide TV came out
    // 5 + 1 with a row of dead glass beside the orphan. Pick the column count
    // that spreads them evenly instead (6 -> 3x2, 7 -> 4+3), never more columns
    // than auto-fit would have allowed, so cards stay above their minimum width.
    useLayoutEffect(() => {
        const el = gridRef.current;
        const n = visibleDoors.length;
        if (!el || n === 0) { setGridCols(null); return; }
        const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
        const minW = Math.min(460, Math.max(240, window.innerWidth * 0.24));  // the CSS clamp()
        const maxCols = Math.max(1, Math.floor((el.clientWidth + gap) / (minW + gap)));
        const rows = Math.ceil(n / maxCols);
        setGridCols(Math.min(n, Math.max(1, Math.ceil(n / rows))));
    }, [visibleDoors.length, resizeKey, tierIdx]);

    return (
        <div id="db-page" className={embedded ? 'db-embedded' : undefined} ref={pageRef}>
            <style>{BOARD_CSS}</style>
            <h1 className="sr-only">
                Dock worker board — one card per dock door with its current and on-deck truck,
                plus a waiting-for-door queue. Arrived trucks are green, expected trucks dim.
            </h1>

            {board === null ? (
                <div id="db-root">
                    <div className="db-message">
                        {error ? error : 'Loading board…'}
                    </div>
                </div>
            ) : (
                <div id="db-root">
                    <div className="db-head">
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, minWidth: 0 }}>
                            <span className="db-title">{board.facilityName ?? board.facilityCode}</span>
                            <span className="db-sub">
                                {visibleDoors.length === 0
                                    ? (doors.length === 0 ? 'no doors' : `${doors.length} doors · all open`)
                                    : pageSubtitle(visibleDoors)}
                            </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(12px,1.6vw,26px)' }}>
                            {/* ⭐ F114 — the same count as the check-in panel's tally: every truck
                                on the clock across ALL doors (not just this page) and the holding
                                strip; red the moment any one is past its free time. */}
                            {detentionCount.total > 0 && (
                                <span className={`db-det ${detentionCount.red > 0 ? 'db-det-over' : detentionCount.warn > 0 ? 'db-det-near' : 'db-det-ok'}`}
                                      title={`${detentionCount.total} on the clock · ${detentionCount.warn} orange · ${detentionCount.red - detentionCount.over} red · ${detentionCount.over} in detention`}>
                                    <Timer className="det-ico" aria-hidden="true" />{detentionCount.total}
                                </span>
                            )}
                            <span className={isStale || error ? 'db-updated db-stale' : 'db-updated'}>
                                ↻ updated {fmtStamp(board.generatedUtc, tz)}
                                {(isStale || error) && ' · reconnecting'}
                            </span>
                            <span className="db-clock">{fmtClock(new Date(nowMs), tz)}</span>
                        </div>
                    </div>

                    <div
                        id="db-grid"
                        ref={gridRef}
                        className={TIERS[tierIdx]}
                        style={gridCols ? { gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` } : undefined}
                    >
                        {visibleDoors.length === 0
                            ? (
                                <div className="db-message">
                                    {doors.length === 0
                                        ? 'No doors configured for this facility.'
                                        : 'No trucks at the dock right now.'}
                                </div>
                            )
                            : visibleDoors.map((d) => (
                                <DoorCard key={d.doorId} door={d} tz={tz} showReserved={showReserved} nowMs={nowMs}
                                          /* ⚠️ `?? 0` — an older payload has no threshold, and the
                                             board must then draw NO alerts rather than every one.
                                             ** AN ABSENT THRESHOLD IS NOT A THRESHOLD OF ZERO. ** */
                                          alertMinutes={board?.doorIdleAlertMinutes ?? 0}
                                          detention={board?.detention ?? null} />
                            ))}
                    </div>

                    {showOpen && openDoors.length > 0 && (
                        <div id="db-open-wrap">
                            <div className="open-title">
                                <span className="h">Open doors</span>
                                <span className="c">({openDoors.length})</span>
                            </div>
                            <div id="db-open">
                                {openDoors.map((d) => (
                                    <span key={d.doorId} className="opendoor">
                                        <DoorNumber label={d.doorLabel} />
                                        {/* An open door that belongs to a client is not
                                            free for anyone — say so on the chip. */}
                                        {d.dedicatedTo && <DedicationChip label={d.dedicatedTo} />}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {holding.length > 0 && (
                        <div id="db-holding-wrap">
                            <div className="holding-title">
                                <span style={{ color: '#e0a63c' }}>⏱</span>
                                {/**
                                  * ⭐⭐ RENAMED AND WIDENED (Pete, 2026-09-24):
                                  * > *"On the bottom, lets change On Today's Schedule -
                                  * > Not Arrived Yet. This should include the Requested
                                  * > Appointments too on today's schedule."*
                                  *
                                  * ⚠️ THE NAME CHANGE IS THE SMALL HALF. Widening the
                                  * list to REQUESTED changes what the section MEANS —
                                  * from "approved and waiting for a door" to
                                  * "everything booked today that has not turned up",
                                  * which is the question a floor lead actually asks.
                                  * ⛔ THAT HALF IS SERVER-SIDE (`board.holding`); this
                                  * is only the label. ** A HEADING THAT PROMISES MORE
                                  * THAN THE QUERY RETURNS IS A LIE WITH A TIMESTAMP
                                  * ON IT. **
                                  */}
                                <span className="h">On Today's Schedule — Not Arrived Yet</span>
                                <span className="c">({holding.length})</span>
                            </div>
                            <div id="db-holding">
                                {holding.map((a) => <HoldingCard key={a.appointmentId} appt={a} tz={tz} nowMs={nowMs} detention={board?.detention ?? null} />)}
                            </div>
                        </div>
                    )}

                    <div className="legend">
                        <span><span className="sw" style={{ background: 'rgb(var(--board-arrived))' }} /> Arrived — needs a worker</span>
                        <span className="legend-work"><WorkingTruck /> Being worked</span>
                        <span className="legend-work"><PausedTruck /> Paused — nobody on it</span>
                        {/* ⚠️ THE SWATCH NOW NAMES THE PILL, NOT THE CARD. Since the
                            finished card keeps the arrived green, a legend entry
                            implying a whole card turns this colour would be a key to
                            something the board no longer draws.
                              ** A LEGEND THAT DESCRIBES A TREATMENT THAT HAS MOVED IS
                                 WORSE THAN NO LEGEND: IT IS CONFIDENTLY WRONG. ** */}
                        <span><span className="sw" style={{ background: 'rgb(var(--board-worked))' }} /> UNLOADED / LOADED — done, awaiting departure</span>
                        <span><span className="sw" style={{ background: 'rgb(var(--board-approved))' }} /> Expected</span>
                        <span><span className="sw sw-blk" /> Out of service</span>
                        {/* Only when the screen actually shows them. A key entry for a
                            card that can never appear on this board is noise on a wall
                            display, where every line competes with a truck for space. */}
                        {standDownMinutes > 0 && (
                            <>
                                <span><span className="sw sw-noshow" /> No-show</span>
                                <span><span className="sw sw-cancelled" /> Cancelled</span>
                            </>
                        )}
                        <span>
                            <span className="arrow" style={{ color: '#e0a63c' }}>↓</span> Unload
                            &nbsp;·&nbsp;
                            <span className="arrow" style={{ color: '#5aa9ef' }}>↑</span> Load
                        </span>
                        {activeDoors.length > 0 && (
                            <span style={{ marginLeft: 'auto' }}>
                                page {pageIndex + 1} of {pageCount}
                                {pageCount > 1 && ` · rotates ${rotateSeconds}s`}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default DockBoardPage;

/**
 * Styles ported verbatim from the approved mockup, scoped under #db-page /
 * #db-root so the generic class names (.card, .body, .meta) can't collide with
 * the rest of the app.
 */
const BOARD_CSS = `
#db-page { position:fixed; inset:0; overflow:auto; background:#05070c; padding:16px;
           font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; z-index:50; }
#db-page * { box-sizing:border-box; }
/* F161: inside the Scheduler it fills the content area under the tab line, not the window. */
#db-page.db-embedded { position:relative; inset:auto; height:100%; z-index:auto; }
/* Fills the screen: a wall TV showing 11 doors in the top third and black
   below is wasted glass. Column layout so the door grid takes the slack. */
#db-root { background:#0e131c; border-radius:12px; padding:clamp(12px,1.6vw,22px); color:#e8ecf2;
        min-height:100%; display:flex; flex-direction:column; }

#db-root .db-head { display:flex; align-items:center; justify-content:space-between; gap:16px;
           border-bottom:1px solid #223047; padding-bottom:clamp(8px,1vw,14px);
           margin-bottom:clamp(10px,1.2vw,16px); flex-wrap:wrap; }
#db-root .db-title { font-size:clamp(20px,2.4vw,34px); font-weight:500; letter-spacing:0.3px; }
#db-root .db-sub   { font-size:clamp(12px,1.1vw,16px); color:#8194ab; }
#db-root .db-updated { font-size:clamp(11px,1vw,15px); color:#8194ab; }
#db-root .db-stale { color:#e0a63c; }
#db-root .db-clock { font-size:clamp(22px,2.6vw,36px); font-weight:500; font-variant-numeric:tabular-nums; }
#db-root .db-message { color:#8194ab; font-size:clamp(14px,1.3vw,20px); padding:clamp(20px,4vw,60px); text-align:center; }

#db-root #db-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(clamp(240px,24vw,460px),1fr));
           gap:clamp(8px,0.9vw,13px);
           /* Take the leftover vertical space and split it evenly across rows,
              so cards grow to fill a TV instead of huddling at the top. */
           flex:1 1 auto; grid-auto-rows:minmax(min-content,1fr); align-content:stretch; }

/* ---------------------------------------------------------------------------
   TYPE SCALE. Every size on a card is a variable so the board can re-tune the
   whole thing per tier instead of restating twenty rules. Read from 15-40 ft:
   the fewer cards on screen, the bigger everything gets, because the card is
   physically bigger and the approved 12-up sizes leave it mostly empty.

     base   7+ cards   the approved mockup sizes
     roomy  3-6 cards  ~1.3x
     sparse 1-2 cards  ~1.6x
   --------------------------------------------------------------------------- */
#db-root {
        --f-doorw:   clamp(64px,6vw,92px);
        --f-doorlbl: clamp(9px,0.8vw,12px);
        --f-doornum: clamp(30px,3.4vw,50px);
        --f-doorpre: clamp(13px,1.4vw,21px);
        --f-client:  clamp(16px,1.5vw,23px);
        --f-meta:    clamp(12px,1.1vw,16px);
        --f-facts:   clamp(11px,1vw,15px);
        --f-pill:    clamp(10px,0.85vw,13px);
        --f-pillpad: 2px 9px;
        --f-ondeck:  clamp(10px,0.85vw,12px);
        --f-truck:   clamp(46px,4.6vw,88px);
        --f-blk:     clamp(15px,1.4vw,22px);
        --f-blksub:  clamp(12px,1.1vw,17px);
        --f-ded:     clamp(9px,0.85vw,13px);
        /* ! SCALES WITH THE CARD, like every other size on this board. A fixed px
             stamp would swallow a dense board and vanish on a roomy one. */
        --f-stamp:   clamp(20px,2.3vw,40px); }

#db-root #db-grid.roomy {
        --f-doorw:   clamp(80px,7.4vw,124px);
        --f-doorlbl: clamp(10px,0.95vw,15px);
        --f-doornum: clamp(38px,4.4vw,66px);
        --f-doorpre: clamp(16px,1.8vw,28px);
        --f-client:  clamp(20px,1.95vw,31px);
        --f-meta:    clamp(14px,1.35vw,21px);
        --f-facts:   clamp(13px,1.3vw,20px);
        --f-pill:    clamp(12px,1.15vw,18px);
        --f-pillpad: 3px 11px;
        --f-ondeck:  clamp(11px,1.05vw,16px);
        --f-truck:   clamp(52px,5.4vw,104px);
        --f-blk:     clamp(19px,1.85vw,29px);
        --f-blksub:  clamp(15px,1.45vw,22px);
        --f-ded:     clamp(11px,1.05vw,16px);
        --f-stamp:   clamp(24px,2.9vw,50px); }

#db-root #db-grid.sparse {
        --f-doorw:   clamp(96px,9vw,150px);
        --f-doorlbl: clamp(11px,1.1vw,17px);
        --f-doornum: clamp(40px,5vw,80px);
        --f-doorpre: clamp(18px,2vw,32px);
        --f-client:  clamp(22px,2.3vw,38px);
        --f-meta:    clamp(15px,1.5vw,24px);
        --f-facts:   clamp(14px,1.4vw,22px);
        --f-pill:    clamp(13px,1.3vw,20px);
        --f-pillpad: 4px 14px;
        --f-ondeck:  clamp(12px,1.15vw,18px);
        --f-truck:   clamp(56px,6vw,120px);
        --f-blk:     clamp(23px,2.2vw,36px);
        --f-blksub:  clamp(17px,1.6vw,26px);
        --f-ded:     clamp(12px,1.2vw,19px);
        --f-stamp:   clamp(28px,3.4vw,62px); }

/* ! position:relative IS NEW AND IT IS WHAT THE STAMP HANGS OFF. overflow:hidden
     was already here for the rounded corners, and it now does a second job: it clips
     the rotated text to the card instead of letting it run across its neighbours. */
#db-root .card { display:flex; border-radius:10px; overflow:hidden; position:relative; }

/* ---- THE DONE STAMP. See DoneStamp() for why it exists and why the angle is
   constant.
   ! CENTRED BY FLEX RATHER THAN POSITIONED BY HAND. The text length differs between
     LOADED and UNLOADED, so any left/top arithmetic would centre one and not the
     other.
   ! THE SHADOW IS NOT DECORATION. White on the arrived green is legible; white over
     the white client name underneath it is not, and the dark outline is what keeps
     BOTH readable -- which is the whole point of keeping the other status items.
   ! opacity .92 rather than 1: enough to read the facts through it at arm's length,
     enough to shout at forty feet. */
#db-root .stamp { position:absolute; inset:0; z-index:5; pointer-events:none;
        display:flex; align-items:center; justify-content:center; }
/* ---- THE DONE STAMP IS BOUND TO THE CURRENT TRUCK (Pete, 2026-09-24) ----------
   "The LOADED tile on DD46 is going across the on deck card too."
   ! .cur IS THE CONTAINING BLOCK NOW. The stamp keeps inset:0 from the rule above,
     so it fills exactly the current truck's block and stops where the ON DECK
     divider starts. On an unsplit card .cur is the whole body, so those cards are
     pixel-identical to before.
   ! ~~A TWO-ROW GRID ON .body~~ WAS THE FIRST ATTEMPT AND IT WAS WRONG. .body also
     holds .blocked and .resv, which have no explicit placement; pinning .cur to row
     1 would have auto-placed the BLOCKED block BELOW the on-deck truck instead of
     above everything, silently reordering the card.
     ** AN EXPLICIT GRID ROW IS ALSO A STATEMENT ABOUT EVERY SIBLING THAT HAS NONE. **
   ! THE NEGATIVE LEFT IS WHAT KEEPS IT CROSSING THE DOOR COLUMN, which was the
     original design and still looks right. --f-doorw is the door column's own
     min-width, so the two cannot drift apart in the stylesheet.
     ! A DEDICATION CHIP CAN PUSH THAT COLUMN WIDER THAN ITS MINIMUM, and then this
       offset is short by the difference. Cosmetic only, clipped by .card's
       overflow:hidden, and preferable to measuring a column in JS every frame.
   ! .stamp ABOVE STAYS CARD-LEVEL FOR THE ALERT, which is about the DOOR standing
     idle rather than about either truck. */
#db-root .cur { position:relative; }
#db-root .cur > .stamp { left:calc(-1 * var(--f-doorw)); overflow:hidden; }
/* THE ALERT STAMP. Red band behind the word, because a rotated word alone on a green
   card is a label; with a band behind it, it is an interruption.
   ! The pulse is on the WHOLE stamp so the band and the word breathe together - two
     elements pulsing out of phase reads as a rendering fault.
   ! REDUCED MOTION KEEPS THE BAND AND DROPS THE PULSE. The alert must survive a
     viewer who cannot tolerate motion, which is why color and word carry it anyway. */
#db-root .stamp-alert { animation:db-alert-pulse 1.1s ease-in-out infinite; }
#db-root .stamp-alert span { background:rgba(190,18,60,0.92); color:#fff;
        padding:0.12em 0.5em; border-radius:6px;
        box-shadow:0 2px 18px rgba(0,0,0,0.55); }
@keyframes db-alert-pulse { 0%,100% { opacity:1; } 50% { opacity:0.35; } }
@media (prefers-reduced-motion: reduce) { #db-root .stamp-alert { animation:none; } }
#db-root .stamp span { transform:rotate(-20deg); white-space:nowrap;
        font-size:var(--f-stamp); font-weight:800; letter-spacing:0.12em;
        color:#ffffff; opacity:0.92;
        text-shadow:0 2px 10px rgba(2,10,6,0.85), 0 0 3px rgba(2,10,6,0.9); }
@media (prefers-reduced-motion: no-preference) { /* no animation here - a stamp does not move */ }
/* The board palette is DATA (dock.StatusColor, migration 135) — these read the
   --board-* variables set at runtime, with the shipped color as the literal
   fallback so an offline TV still paints. Card tints are derived from the same
   variable via color-mix, with a static fallback for older engines. */
#db-root .card-arrived { background:#10271c;
        background:color-mix(in srgb, rgb(var(--board-arrived)) 16%, #0b0f16);
        border:1px solid rgb(var(--board-arrived)); border-left:7px solid rgb(var(--board-arrived)); }
/* BEING WORKED keeps the ARRIVED color on purpose — the truck IS still
   arrived. The orbiting-truck mark carries "being worked", so the board never
   says with color what it already says with motion. */
#db-root .card-working { background:#10271c;
        background:color-mix(in srgb, rgb(var(--board-arrived)) 16%, #0b0f16);
        border:1px solid rgb(var(--board-arrived)); border-left:7px solid rgb(var(--board-arrived)); }
/* PAUSED keeps the arrived treatment for the reason card-working does: the truck
   is still arrived and the static pause mark carries "nobody is on it". ⛔ NOT a
   new hue — this board already spends amber on RESERVED and HOLDING, and a third
   amber would make all three mean "look here, something". */
#db-root .card-paused { background:#10271c;
        background:color-mix(in srgb, rgb(var(--board-arrived)) 16%, #0b0f16);
        border:1px solid rgb(var(--board-arrived)); border-left:7px solid rgb(var(--board-arrived)); }
#db-root .card-idle    { background:#151b26; border:1px solid rgb(var(--board-approved));
        border-left:7px solid rgb(var(--board-approved)); }
/* OUT OF SERVICE — the scheduler's exact block treatment: slate, with the 45°
   hatch that already means "this is not an appointment, this door is shut". A
   dock worker who has seen the calendar recognises it without a legend. */
/* RESERVED — dedicated to a client, nothing at it. Amber edge (the dedication
   color everywhere else), dark body: it's a standing claim on the door, not
   work anyone can do. */
#db-root .card-reserved { background:#191a1e;
        background:color-mix(in srgb, #b45309 8%, #0b0f16);
        border:1px solid #7c5312; border-left:7px solid #b45309; }
#db-root .resv { padding-top:2px; }
#db-root .resv-lbl { font-size:var(--f-pill); font-weight:600; letter-spacing:1.2px; color:#e0a63c; }
#db-root .resv-for { font-size:var(--f-client); font-weight:500; color:#fcd34d; line-height:1.15; }
#db-root .resv-sub { font-size:var(--f-facts); color:#8194ab; margin-top:2px; }

#db-root .card-blocked { background:#252f3e; border:1px solid #64748b; border-left:7px solid #64748b;
        background-image:repeating-linear-gradient(45deg,
                rgba(15,23,42,0.55) 0, rgba(15,23,42,0.55) 4px,
                transparent 4px, transparent 13px); }
/* STAND-DOWN — a no-show or cancellation held on the board briefly so its
   disappearance is announced rather than silent (migration 137).
   Deliberately NOT palette-driven, unlike every other card color: a stand-down
   is a board-only idea covering TWO scheduler statuses (NOSHOW and CANCELLED),
   so there is no single dock.StatusColor row it could read without picking one
   of them arbitrarily.
   Desaturated maroon: it must not read as an alarm (nobody needs to act) and it
   must not be mistaken at forty feet for the slate hatch of an out-of-service
   door, which is the only other "this is not workable" card on the wall. */
/* TWO FLAVOURS, ONE SHAPE. --sd is the only thing that differs, and it comes
   from dock.StatusColor (migration 138) so the grey can be retuned in Settings ->
   Status Colors if it reads too close to out-of-service on the real TV. */
#db-root .sd-noshow    { --sd: var(--board-noshow); }
#db-root .sd-cancelled { --sd: var(--board-cancelled); }
/* The fallback is a var() DEFAULT, not a declaration on .card-standdown.
   Declaring --sd here instead cost a measured red: .sd-cancelled and
   .card-standdown have identical specificity and sit on the SAME element, so
   source order decided it and every cancelled card painted crimson. A var()
   fallback can't lose that fight. Crimson is the default because it's the
   louder of the two — the safer one to be wrong in. */
#db-root .card-standdown { background:#1d1418;
        background:color-mix(in srgb, rgb(var(--sd, var(--board-noshow))) 13%, #0b0f16);
        border:1px solid color-mix(in srgb, rgb(var(--sd, var(--board-noshow))) 42%, #0b0f16);
        border-left:7px solid rgb(var(--sd, var(--board-noshow))); }
/* Neutral light text on the door block: it sits on crimson AND on grey, and a
   pink-tinted label that looked right on one washed out on the other. */
#db-root .door-standdown { background:color-mix(in srgb, rgb(var(--sd, var(--board-noshow))) 42%, #0b0f16);
        color:#eaeef3; }
#db-root .pill-standdown { background:rgb(var(--sd, var(--board-noshow))); color:#fff;
        font-weight:600; letter-spacing:0.4px; }
/* Struck through, like the blocked door number: the single clearest way to say
   "this one is off" without needing the legend. */
#db-root .client-stood { text-decoration:line-through; text-decoration-thickness:0.07em;
        text-decoration-color:#c48a9c; opacity:0.85; }
#db-root .hcard-standdown {
        border-color:color-mix(in srgb, rgb(var(--sd, var(--board-noshow))) 42%, #0b0f16);
        background:color-mix(in srgb, rgb(var(--sd, var(--board-noshow))) 13%, #0b0f16); }

/* Worked, still at the door, waiting on departure: present but receded. */
/* ---- ⭐⭐ A FINISHED TRUCK KEEPS THE ARRIVED COLOUR (Pete, 2026-09-24) -------
   > *"Not sure the color change is doing anything for me."*

   ⛔⛔ THE DIM GREEN SAID "STOP LOOKING AT THIS" ABOUT A TRUCK STILL AT THE DOOR.
   The work is over; the LOAD is not. It is occupying a door and waiting for somebody
   to release it, and fading it out is the opposite of what the floor needs.
     ** A STATE THAT STILL REQUIRES AN ACTION MUST NOT BE DRAWN AS A STATE THAT IS
        FINISHED WITH. **

   ⭐ SO THE DOOR BLOCK NOW CHANGES COLOUR EXACTLY ONCE — ON DEPARTURE, which is when
   the door actually frees up. Everything before that is one green.
   ⚠️ --board-worked IS NOT ORPHANED: .pill-done still uses it, so UNLOADED reads
   as a different chip from ARRIVED beside it, and the palette row stays meaningful
   and tunable in Settings -> Status Colors.
   ⛔⛤ AND NO BACKTICKS IN THIS COMMENT. It lives inside BOARD_CSS, which is a
   TEMPLATE LITERAL -- a backtick here ends the string and the file stops parsing.
   This is the fourth time that has been written in this codebase.
     ** A COMMENT ABOUT A SYNTAX CANNOT CONTAIN THAT SYNTAX. ** */
#db-root .card-done    { background:#10271c;
        background:color-mix(in srgb, rgb(var(--board-arrived)) 16%, #0b0f16);
        border:1px solid rgb(var(--board-arrived)); border-left:7px solid rgb(var(--board-arrived)); }

/* Grows with the code (Brighton's "DD119" needs more room than "31") but never
   shrinks below the approved block width. */
/* THREE ROWS, middle one auto: the door number sits in the middle row and the
   two 1fr rows above and below it are always equal, so the number lands at the
   SAME HEIGHT on every card whether or not a truck is being worked. The mark
   centres itself in the row above. A flexbox that reflows when the mark appears
   makes the numbers stagger across the wall, which is the one thing a row of
   door signs must not do. */
#db-root .door { min-width:var(--f-doorw); padding:clamp(6px,0.8vw,14px) clamp(5px,0.5vw,9px); flex-shrink:0;
        display:grid; grid-template-rows:minmax(0,1fr) auto minmax(0,1fr);
        justify-items:center; align-items:center; font-variant-numeric:tabular-nums; }
/* The door identity (label + number + dedication) travels as one block, pinned
   to the middle row. Pinned EXPLICITLY: with auto-placement it would slide up
   into row 1 on cards that have no work mark, which is the bug this fixes. */
#db-root .door-id { grid-row:2; display:flex; flex-direction:column; align-items:center; }
#db-root .door > .worktruck { grid-row:1; }
#db-root .door-arrived { background:rgb(var(--board-arrived)); color:#052417; }
#db-root .door-working { background:rgb(var(--board-arrived)); color:#052417; }
#db-root .door-paused  { background:rgb(var(--board-arrived)); color:#052417; }
/* F129: the detention colors on the door column, after the status colors so they win. */
#db-root .door-det-near { background:#fb923c; color:#1c0d02; }
#db-root .door-det-over { background:#dc2626; color:#fff; }
#db-root .door-idle    { background:rgb(var(--board-approved)); color:#c3ced9; }
#db-root .door-done    { background:rgb(var(--board-arrived)); color:#052417; }
#db-root .door-blocked { background:#64748b; color:#f1f5f9; }
/* CROSSED OUT. The hatch says "not an appointment"; the strike says "not this
   door". Applied to the parts, not the flex parent — text-decoration does not
   reliably cross flex items. */
#db-root .door-blocked .door-num,
#db-root .door-blocked .door-pre,
#db-root .door-blocked .door-digits { text-decoration:line-through;
        text-decoration-thickness:0.09em; text-decoration-color:#f8fafc; }
/* An empty door that belongs to a client: amber, and quieter than a truck. */
#db-root .door-reserved { background:#4a4132; color:#fcd34d; }
#db-root .door-lbl { font-size:var(--f-doorlbl); font-weight:500; opacity:0.75; }
#db-root .door-num { font-size:var(--f-doornum); font-weight:500; line-height:1; white-space:nowrap; }
/* Prefix small, digits full-size: "DD" + "119". */
#db-root .door-num-split { display:flex; align-items:baseline; gap:2px; line-height:1; font-weight:500; }
#db-root .door-pre { font-size:var(--f-doorpre); opacity:0.75; letter-spacing:0.5px; }
#db-root .door-digits { font-size:var(--f-doornum); }
#db-root .door-digits-4 { font-size:calc(var(--f-doornum) * 0.78); }
#db-root .door-num-3 { font-size:calc(var(--f-doornum) * 0.82); }
#db-root .door-num-4 { font-size:calc(var(--f-doornum) * 0.68); }
#db-root .door-num-5 { font-size:calc(var(--f-doornum) * 0.58); }

#db-root .body { padding:clamp(7px,0.8vw,11px) clamp(9px,1vw,14px); flex:1; min-width:0; }
#db-root .pills { display:flex; align-items:center; gap:7px; margin-bottom:3px; flex-wrap:wrap; }
#db-root .pill { font-size:var(--f-pill); font-weight:500; padding:var(--f-pillpad); border-radius:20px; white-space:nowrap; }
#db-root .pill-arrived  { background:rgb(var(--board-arrived)); color:#052417; }
#db-root .pill-working  { background:rgb(var(--board-arrived)); color:#052417; }
/* The one place paused is allowed a color of its own: a PILL is read at arm's
   length, not across the floor, so it competes with nothing. Slate, because the
   two ambers on this board already mean "a claim on this door". */
#db-root .pill-paused   { background:#e2e8f0; color:#0f172a; font-weight:600;
        letter-spacing:0.6px; }
#db-root .pill-expected { background:#33404f; color:#aeb9c7; }
#db-root .pill-done     { background:rgb(var(--board-worked)); color:#dff0e8; }
/* ⭐ F114 (Decision 298) — the detention clock: orange inside the free time, red past it
   (the driver card's colors, darkened text for a dark wall). The red OUTLINE is solid on
   purpose: the FLASHING band is the door-idle alert, and two alarms that look alike stop
   being read as either. */
#db-root .pill-det      { display:inline-flex; align-items:center; gap:0.25em; font-weight:600; font-variant-numeric:tabular-nums; }
/* F155: above the orange lead the clock wears the ARRIVED status color (Pete: "Same color as the status - arrived."). */
#db-root .pill-det-ok   { background:rgb(var(--board-arrived)); color:#052417; }
#db-root .pill-det-near { background:#fb923c; color:#1c0d02; }
#db-root .pill-det-over { background:#dc2626; color:#fff; }
#db-root .det-ico       { width:1em; height:1em; flex:none; }
#db-root .card-det-over { box-shadow:inset 0 0 0 3px #dc2626; }
#db-root .hcard-det-over { box-shadow:inset 0 0 0 2px #dc2626; }
#db-root .db-det        { display:inline-flex; align-items:center; gap:0.3em; border-radius:20px; font-weight:700;
                          padding:0.15em 0.6em; font-size:clamp(14px,1.6vw,24px); font-variant-numeric:tabular-nums; }
#db-root .db-det-ok     { background:rgb(var(--board-arrived)); color:#052417; }
#db-root .db-det-near   { background:#fb923c; color:#1c0d02; }
#db-root .db-det-over   { background:#dc2626; color:#fff; }
#db-root .pill-blk      { background:#0f172a; color:#e2e8f0; border:1px solid #94a3b8; font-weight:600; }

/* Out-of-service body copy. Sits above whatever is parked at the door. */
#db-root .blk { margin-bottom:6px; }
#db-root .blk-label { font-size:var(--f-blk); font-weight:500; color:#e8ecf2; }
#db-root .blk-sub { font-size:var(--f-blksub); color:#b3c0d1; letter-spacing:0.3px; }

/* DEDICATED DOOR — the scheduler's amber chip, shrunk for the door block. */
#db-root .ded { display:inline-flex; align-items:center; gap:4px; margin-top:clamp(4px,0.5vw,8px);
        background:#fde68a; color:#78350f; border-radius:5px; font-weight:600; max-width:100%;
        padding:1px clamp(4px,0.5vw,7px); font-size:var(--f-ded); letter-spacing:0.3px; }
#db-root .ded-dot { width:5px; height:5px; border-radius:50%; background:#b45309; flex-shrink:0; }
#db-root .ded-txt { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
#db-root .opendoor .ded { margin-top:0; margin-left:7px; font-size:clamp(8px,0.75vw,11px); }

/* Orbiting-truck mark: arrows rotate, truck stays put. Sized for distance. */
#db-root .worktruck { position:relative; display:inline-flex; flex-shrink:0; color:#ffffff;
        width:var(--f-truck); height:var(--f-truck); }
#db-root .worktruck-ring { position:absolute; inset:0; width:100%; height:100%;
        animation:db-orbit 2.4s linear infinite; transform-origin:50% 50%; }
/* Truck dominant, ring orbiting clear of it - it has to read as a TRUCK at 40 ft. */
#db-root .worktruck-truck { position:absolute; left:23%; top:23%; width:54%; height:54%;
        stroke-width:2.4; }
/* The pause mark fills the same box as the orbiting truck and does NOT animate -
   there is no db-orbit here, and its absence is the whole message. */
#db-root .worktruck-pause { position:absolute; inset:0; width:100%; height:100%; }
@keyframes db-orbit { to { transform:rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { #db-root .worktruck-ring { animation-duration:8s; } }

#db-root .workers { margin-top:3px; font-size:var(--f-facts); }
/* Direction pills are neutral now: blue and amber carry STATUS meaning on this
   board (arrived / holding), so the arrow + word carries direction instead. */
#db-root .pill-load     { background:transparent; border:1px solid #3d4a5c; color:#aeb9c7; }
#db-root .pill-unload   { background:transparent; border:1px solid #3d4a5c; color:#aeb9c7; }
/* ...until work starts, when it brightens and breathes. */
#db-root .pill-active   { background:#ffffff; border-color:#ffffff; color:#10271c; font-weight:600;
        animation:db-breathe 1.6s ease-in-out infinite; }
@keyframes db-breathe { 50% { opacity:0.4; } }
@media (prefers-reduced-motion: reduce) { #db-root .pill-active { animation:none; } }

#db-root .client { font-size:var(--f-client); font-weight:500; line-height:1.1; }
#db-root .client-arrived { color:#eef2f7; }
#db-root .client-idle { color:#c3ccd8; }
#db-root .meta   { font-size:var(--f-meta); }
#db-root .meta-arrived { color:#8fa0b6; }
#db-root .meta-idle { color:#75859a; }
/* ---- THE FACT ROWS ARE GRIDS, NOT WRAPPING FLOWS (Pete, 2026-09-24) ----------
   ! ~~display:flex; flex-wrap:wrap~~ WAS THE BUG HE SPOTTED FROM A SCREENSHOT:
     "plt is in a different spot, started time is different... I think because
     things are wrapping?" Correct. Every cell's position was decided by which of
     its neighbours existed, so five cards read as five layouts.
   ! A GRID GIVES EVERY FIELD ITS OWN COLUMN ON EVERY CARD. An absent value leaves
     the column EMPTY rather than collapsing it, which is the whole point -- a
     collapsing column is a flow wearing a grid's clothes.
   ! minmax(0,Nfr) AND min-width:0, BOTH. A grid track will not shrink below its
     content's min-content width without them, so one long container number would
     push the row past the card edge -- the wrap failure returning by another road.
   ! NO flex-wrap ANYWHERE HERE. If a card is too narrow for its columns the CELLS
     ellipsise; the ROW never becomes two rows. A field that wrapped is what moved
     everything below it. */
#db-root .facts  { display:grid; gap:clamp(10px,1.1vw,16px); margin-top:4px; font-size:var(--f-facts); align-items:baseline; }
#db-root .facts .cell { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
/* Ref/SO | PO | plt -- the pallet count is last and sized to its content, because
   it is three characters and giving it a third of the row wastes the width the two
   identifiers need. */
#db-root .facts-id  { grid-template-columns:minmax(0,1fr) minmax(0,1fr) auto; }
/* Arr | Started | Finished | Elapsed -- four equal columns so the same word is under
   the same word on the card beside it, which is the entire request. */
#db-root .facts-time { grid-template-columns:repeat(4, minmax(0,1fr)); }
/* TRL | CONT | Truck n of m */
#db-root .facts-veh { grid-template-columns:minmax(0,1fr) minmax(0,1fr) auto; }
/* ! A STAND-DOWN COLLAPSES ITS TIME ROW TO ONE CELL. The 1/-1 span covers whatever
     the track count is, so this survives a fifth time column being added later.
   ! NO BACKTICKS IN THIS COMMENT, AND THE FIRST DRAFT HAD TWO OF THEM AROUND THAT
     SPAN VALUE. BOARD_CSS is a template literal; a backtick inside it ENDS the
     string, and the build failed with "',' expected" 200 lines away in what the
     parser then read as code. SEVENTH TIME THIS EXACT MISTAKE HAS BEEN MADE.
     ** A COMMENT ABOUT A SYNTAX CANNOT CONTAIN THAT SYNTAX. ** */
#db-root .facts .cell-span { grid-column:1 / -1; }
#db-root .facts-arrived { color:#8fa0b6; }
#db-root .facts-idle { color:#75859a; }
#db-root .strong-arrived { color:#eef2f7; font-weight:500; }
/* The booked value beside a disagreeing arrival. Amber because it is worth
   noticing and NOT red because it is not an error - a carrier sending a different
   trailer is an ordinary Tuesday. */
#db-root .mismatch { color:#e0a63c; }
#db-root .strong-idle { color:#c3ccd8; font-weight:500; }
/**
 * ⚠️ THE DIVIDER IS A TRANSLUCENT WHITE, NOT A SLATE HEX. Pete, 2026-08-24: *"the
 * line separating the Hellman and Traffix cards needs to be white or something,
 * can't see it now."*
 *
 * ⛔ #2c3849 WAS PICKED AGAINST THE BOARD'S BACKGROUND AND NOTHING ELSE. This line
 * is drawn INSIDE a door card, and a card is green when a truck has arrived, amber
 * when it is late, red when it is a no-show — so a fixed dark slate disappears on
 * three of the four states it has to work on. ⭐ rgba white takes its contrast from
 * whatever it is drawn over, which is the only way one value can be right on all
 * of them.
 */
#db-root .ondeck { border-top:1px dashed rgba(255,255,255,0.38); margin-top:8px; padding-top:7px; opacity:0.92; }
#db-root .ondeck-lbl { font-size:var(--f-ondeck); color:#6b7c92; letter-spacing:0.5px; margin-bottom:3px; }
#db-root .empty { color:#5a6b80; font-size:clamp(12px,1.1vw,16px); padding:6px 0; }

/* OPEN DOORS — every door with nothing at it, collapsed to just its number.
   Same grey door-block treatment as a card's door bar so it reads as "the door,
   with no truck", not as a new kind of object; sized down to a chip because an
   empty door is information, not the thing anyone is walking toward. */
#db-root #db-open-wrap { margin-top:clamp(10px,1.2vw,16px); border-top:1px solid #223047;
        padding-top:clamp(8px,1vw,13px); flex:0 0 auto; }
#db-root .open-title { display:flex; align-items:center; gap:8px; margin-bottom:8px; }
#db-root .open-title span.h { font-size:clamp(12px,1.1vw,16px); font-weight:500; color:#8194ab; letter-spacing:0.4px; }
#db-root .open-title span.c { font-size:clamp(11px,1vw,14px); color:#63748b; }
#db-root #db-open { display:flex; flex-wrap:wrap; gap:clamp(6px,0.7vw,10px); }
#db-root .opendoor { display:inline-flex; align-items:center; justify-content:center;
        background:#1b2331; border:1px solid #2c3849; border-radius:8px; color:#8fa0b6;
        padding:clamp(4px,0.5vw,7px) clamp(9px,1vw,14px); font-variant-numeric:tabular-nums; }
#db-root .opendoor .door-num,
#db-root .opendoor .door-digits    { font-size:clamp(15px,1.5vw,23px); }
#db-root .opendoor .door-digits-4  { font-size:clamp(13px,1.3vw,20px); }
#db-root .opendoor .door-num-3     { font-size:clamp(14px,1.4vw,21px); }
#db-root .opendoor .door-num-4     { font-size:clamp(13px,1.3vw,19px); }
#db-root .opendoor .door-num-5     { font-size:clamp(12px,1.2vw,17px); }
#db-root .opendoor .door-pre       { font-size:clamp(9px,0.9vw,13px); }

#db-root #db-holding-wrap { margin-top:clamp(10px,1.2vw,16px); border-top:1px solid #223047; padding-top:clamp(8px,1vw,13px); }
#db-root .holding-title { display:flex; align-items:center; gap:8px; margin-bottom:8px; }
#db-root .holding-title span.h { font-size:clamp(12px,1.1vw,16px); font-weight:500; color:#e0a63c; letter-spacing:0.4px; }
#db-root .holding-title span.c { font-size:clamp(11px,1vw,14px); color:#8194ab; }
#db-root #db-holding { display:grid; grid-template-columns:repeat(auto-fill,minmax(clamp(150px,15vw,210px),1fr)); gap:clamp(6px,0.7vw,10px); }
#db-root .hcard { background:#1a1a20; border:1px solid #3a3320;
         border-left:5px solid rgb(var(--board-holding)); border-radius:9px;
         padding:clamp(7px,0.8vw,11px) clamp(8px,0.9vw,12px); }
#db-root .hcard .hc-pills { display:flex; align-items:center; gap:6px; margin-bottom:3px; flex-wrap:wrap; }
#db-root .hcard .hc-arr { font-size:clamp(10px,0.85vw,12px); color:#8fa0b6; }
#db-root .hcard .hc-client { font-size:clamp(14px,1.3vw,19px); font-weight:500; line-height:1.1; color:#eef2f7; }
#db-root .hcard .hc-meta { font-size:clamp(11px,1vw,14px); color:#8fa0b6; }
#db-root .hcard .hc-meta strong { color:#c3ced9; font-weight:500; }

#db-root .legend { display:flex; gap:clamp(14px,1.6vw,26px); margin-top:clamp(10px,1.2vw,16px);
          padding-top:clamp(8px,1vw,12px); border-top:1px solid #223047;
          font-size:clamp(11px,1vw,14px); color:#8194ab; flex-wrap:wrap; }
#db-root .sw { display:inline-block; width:11px; height:11px; border-radius:2px; vertical-align:-1px; }
#db-root .sw-noshow    { background:rgb(var(--board-noshow)); }
#db-root .sw-cancelled { background:rgb(var(--board-cancelled)); }
#db-root .sw-blk { background:#64748b;
        background-image:repeating-linear-gradient(45deg,
                rgba(15,23,42,0.6) 0, rgba(15,23,42,0.6) 2px, transparent 2px, transparent 5px); }
/* Legend copy of the mark, scaled down to sit on a text line. */
#db-root .legend-work { display:inline-flex; align-items:center; gap:6px; }
#db-root .legend-work .worktruck { width:clamp(16px,1.3vw,22px); height:clamp(16px,1.3vw,22px); }
#db-root .arrow { font-weight:700; }
`;
